import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Animated, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { withCardFee } from '@suitescanner/pricing';
import { api } from '~/api';
import { track } from '~/lib/analytics';
import type { Place, SearchBarValue } from '~/components/ReferenceSearchBar';
import { exactHotelMatch, findHotelPlace } from '~/lib/destinations';
import { NavSearchBar } from '~/components/NavSearchBar';
import { formatRangeLabelCompact, formatUtcDateShort } from '~/lib/dateUtils';
import { getAmenityIcon } from '~/lib/amenityIcons';
import { DEFAULT_GUESTS, guestsFromOccupancy } from '~/lib/guests';
import { resolveRoom } from '@suitescanner/etg-rooms';
import { resolveImageUrl } from '~/lib/images';
import { formatMoney } from '~/lib/money';
import { formatHotelPageReviewScore, hotelPageReviewScoreColor } from '~/lib/hotelReviews';
import { decodeOccupancy, encodeOccupancy } from '~/lib/occupancy';
import { getDefaultResidency, normalizeResidency } from '~/lib/residency';
import { formatCancellationSummary, isFreeCancellation, openFreeCancellationBefore } from '~/lib/cancellation';
import { formatExactAmount } from '~/lib/currency';
import { formatTaxName } from '~/lib/taxes';
import { useReducedMotion } from '~/hooks/useReducedMotion';
import { previewPriceIntelligence } from '~/lib/priceIntelligence.mock';
import { colors, control, fontFamily, maxContentWidth, price, radii, shadows, space, spacing, type, typeCompact } from '~/ui/tokens';
import { useMember } from '~/stores/useMember';
import { useCurrency } from '~/stores/useCurrency';
import { Footer } from '../../components/Footer';
import { RoomDetailSheet } from '../../components/RoomDetailSheet';
import { Button, EmptyState, LoadingState } from '../../components/ui';
import { mockHotels, mockHotelContent } from '~/lib/api/mockData';
import type { HotelContent, HotelReviewItem, HotelSummary, ImageSource, Money, Rate } from '../../lib/api/types';

type BedGroup = {
  key: string;
  bedType: string | null;
  rates: Rate[];
};

type RoomGroup = {
  key: string;
  title: string;
  sleeps: number | null;
  sizeSqm: number | null;
  /** Amenities genuinely shared by every rate in every bed group here — an
   * intersection, not a union, so the master card never claims an amenity
   * that only one bed configuration actually has. */
  amenities: string[];
  images: ImageSource[];
  bedGroups: BedGroup[];
};

type RoomFilters = {
  freeCancellation: boolean;
  breakfast: boolean;
  payAtProperty: boolean;
};

function mealLabel(rate: Rate): string {
  if (rate.meal.hasBreakfast) return 'Breakfast included';
  const normalized = rate.meal.value?.toLowerCase().replace(/[-\s]+/g, '_');
  if (!normalized || normalized === 'nomeal' || normalized === 'no_meal' || normalized === 'room_only') return 'Room only';
  if (normalized === 'half_board') return 'Half board';
  if (normalized === 'full_board') return 'Full board';
  if (normalized === 'all_inclusive') return 'All inclusive';
  return rate.meal.value.replace(/_/g, ' ');
}

function bedTypeLabel(bedType: string | null): string {
  return bedType && bedType.trim() ? bedType.trim() : 'Bed type varies';
}

function bedTypeKey(bedType: string | null): string {
  return bedTypeLabel(bedType).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Master group: room type (+occupancy, so materially different capacities
 * never collapse together) → bed-type subgroup → individual rate plans.
 * Room type comes from `roomGroupName`, which adaptHotel already derives
 * from the supplier's translated main_room_type (or the static room_groups
 * match) rather than the raw, bed/meal-qualified room_name — the closest
 * thing to a stable room identity this ETG integration exposes. */
function roomGroups(rates: Rate[]): RoomGroup[] {
  const groups = new Map<string, { title: string; sleeps: number | null; sizeSqm: number | null; images: ImageSource[]; bedGroups: Map<string, BedGroup> }>();
  rates.forEach((rate) => {
    const title = rate.roomGroupName || rate.roomName.split('(')[0].trim();
    const key = rate.rgHash ?? `${title.toLowerCase()}|${rate.sleeps ?? 'na'}`;
    let group = groups.get(key);
    if (!group) {
      group = { title, sleeps: rate.sleeps ?? null, sizeSqm: rate.roomSizeSqm ?? null, images: rate.images, bedGroups: new Map() };
      groups.set(key, group);
    } else {
      group.sizeSqm ??= rate.roomSizeSqm ?? null;
      if (!group.images.length) group.images = rate.images;
    }
    const bedKey = bedTypeKey(rate.bedType ?? null);
    let bedGroup = group.bedGroups.get(bedKey);
    if (!bedGroup) {
      bedGroup = { key: bedKey, bedType: rate.bedType ?? null, rates: [] };
      group.bedGroups.set(bedKey, bedGroup);
    }
    bedGroup.rates.push(rate);
  });
  return [...groups.entries()].map(([key, group]) => {
    const bedGroups = [...group.bedGroups.values()]
      .map((bedGroup) => ({ ...bedGroup, rates: [...bedGroup.rates].sort((a, b) => Number(a.price.amount) - Number(b.price.amount)) }))
      .sort((a, b) => Number(a.rates[0]?.price.amount ?? 0) - Number(b.rates[0]?.price.amount ?? 0));
    const amenitySets = bedGroups.flatMap((bedGroup) => bedGroup.rates).map((rate) => new Set((rate.amenities ?? []).map((item) => item.trim())));
    const sharedAmenities = amenitySets.length ? [...amenitySets[0]].filter((item) => amenitySets.every((set) => set.has(item))) : [];
    return {
      key,
      title: group.title,
      sleeps: group.sleeps,
      sizeSqm: group.sizeSqm,
      amenities: sharedAmenities,
      images: group.images.slice(0, 3),
      bedGroups,
    };
  });
}

// Most useful, differentiating room attributes first; one item per category
// so synonyms (Wi-Fi / Internet / Free Wi-Fi) never crowd out other facts.
function prioritizedRoomAmenities(amenities: string[]): string[] {
  const excluded = /parking|restaurant|reception|newspaper|garden|smoking area|elevator|conference|pets|pool|spa/i;
  const priority = [
    /balcony|terrace|view/i,
    /shower|bathtub|bathroom/i,
    /air.?condition/i,
    /wi-?fi|internet/i,
    /kitchen|coffee|tea|mini.?bar|refrigerator/i,
  ];
  const seenCategories = new Set<number>();
  const seenLabels = new Set<string>();
  const deduped: string[] = [];
  for (const raw of amenities) {
    const item = raw.trim();
    if (!item || excluded.test(item)) continue;
    const label = item.toLowerCase();
    if (seenLabels.has(label)) continue;
    const category = priority.findIndex((pattern) => pattern.test(item));
    if (category >= 0) {
      if (seenCategories.has(category)) continue;
      seenCategories.add(category);
    }
    seenLabels.add(label);
    deduped.push(item);
  }
  return deduped.sort((left, right) => {
    const leftScore = priority.findIndex((pattern) => pattern.test(left));
    const rightScore = priority.findIndex((pattern) => pattern.test(right));
    return (leftScore < 0 ? 99 : leftScore) - (rightScore < 0 ? 99 : rightScore);
  });
}

const humanize = (value: unknown) => String(value ?? '').replace(/_/g, ' ').trim();

// Turns one `metapolicy_struct` entry's price into text like "USD 55 per room per stay".
function metaPrice(entry: any): string {
  const included = entry?.inclusion === 'included';
  const amount = Number(entry?.price);
  const unit = entry?.price_unit && entry.price_unit !== 'unspecified' ? ` ${humanize(entry.price_unit)}` : '';
  if (included || amount === 0) return 'free';
  if (!Number.isFinite(amount) || amount <= 0) return entry?.inclusion === 'not_included' ? 'charges apply' : '';
  const money = entry?.currency ? `${entry.currency} ${amount}` : String(amount);
  return `${money}${unit}`;
}

// ETG free-text policy notes arrive as small HTML snippets; reduce them to plain lines.
const LIST_ITEM = '\u0001';

function htmlToLines(value: string, markListItems = false): string[] {
  const entities: Record<string, string> = { '&amp;': '&', '&nbsp;': ' ', '&quot;': '"', '&#39;': "'", '&lt;': '<', '&gt;': '>' };
  return value
    .replace(/<li[^>]*>/gi, markListItems ? `\n${LIST_ITEM}` : '\n')
    .replace(/<\/(li|p|div|ul|h\d)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(amp|nbsp|quot|lt|gt|#39);/g, (match) => entities[match] ?? match)
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.replace(LIST_ITEM, '').trim().length > 0);
}

// A short line with no digits or sentence punctuation is a section heading, not a rule.
const isPolicyHeading = (line: string) => line.length <= 40 && !/[\d.:!?]/.test(line);

const POLICY_TOPICS: Array<[RegExp, string, string]> = [
  [/shuttle|airport transfer/i, 'Airport shuttle', 'bus-outline'],
  [/breakfast|lunch|dinner|\bmeals?\b/i, 'Meals', 'restaurant-outline'],
  [/\bpets?\b/i, 'Pets', 'paw-outline'],
  [/parking/i, 'Parking', 'car-outline'],
  [/wi-?fi|internet/i, 'Internet', 'wifi-outline'],
  [/deposit/i, 'Deposits', 'shield-checkmark-outline'],
  [/city tax|tax is imposed by the city/i, 'City tax', 'receipt-outline'],
  [/\btax(es)?\b/i, 'Taxes', 'receipt-outline'],
  [/children|\bchild\b|\bkids?\b|infants?/i, 'Children', 'people-outline'],
  [/check-?in|check-?out|arriv/i, 'Check-in and check-out', 'time-outline'],
  [/\bfees?\b|charges?/i, 'Fees', 'cash-outline'],
];

// Prose that only introduces or caveats a list ("The following fees…", "The above list…").
// On its own it is meaningless, and the facts it points at are shown as their own rows.
const POLICY_BOILERPLATE = /the following|the above|the below|may not be comprehensive|subject to change|charges can vary|we have included all|(?:will|'ll) be asked to pay|charged by the property at/i;

// Free text becomes one row per fact: list items, plus any prose that is not boilerplate.
function splitPolicyText(values: string[]): Array<{ icon: string; title: string; detail: string }> {
  const rows: Array<{ icon: string; title: string; detail: string }> = [];
  const seen = new Set<string>();
  for (const raw of values.flatMap((value) => htmlToLines(value, true))) {
    const isListItem = raw.startsWith(LIST_ITEM);
    const line = raw.replace(LIST_ITEM, '').trim();
    if (!isListItem && (isPolicyHeading(line) || line.endsWith(':') || POLICY_BOILERPLATE.test(line))) continue;
    if (seen.has(line)) continue;
    seen.add(line);
    const topic = POLICY_TOPICS.find(([pattern]) => pattern.test(line));
    rows.push({ icon: topic?.[2] ?? 'information-circle-outline', title: topic?.[1] ?? 'Please note', detail: line });
  }
  return rows;
}

function buildKeyInfo(meta: any, freeText: string[] = []): Array<{ icon: string; title: string; detail: string }> {
  const list = (key: string): any[] => (Array.isArray(meta?.[key]) ? meta[key] : []);
  const rows: Array<{ icon: string; title: string; detail: string }> = [];
  const add = (icon: string, title: string, parts: string[]) => {
    const detail = parts.filter(Boolean).join('; ');
    if (detail) rows.push({ icon, title, detail });
  };
  const cap = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

  add('paw-outline', 'Pets', list('pets').map((pet) => {
    const price = metaPrice(pet);
    return price === 'free' ? 'Pets allowed free of charge' : price === 'charges apply' ? 'Pets allowed, charges apply' : `Pets allowed, ${price}`;
  }));
  add('shield-checkmark-outline', 'Deposit', list('deposit').map((entry) => {
    const kind = entry.deposit_type && entry.deposit_type !== 'unspecified' ? `${cap(humanize(entry.deposit_type))} deposit` : 'Deposit';
    const amount = entry.pricing_method === 'percent' ? `${entry.price}%` : metaPrice(entry);
    return `${kind}: ${amount}${entry.pricing_method === 'percent' && entry.price_unit && entry.price_unit !== 'unspecified' ? ` ${humanize(entry.price_unit)}` : ''}`;
  }));
  add('people-outline', 'Children', list('children').map((child) => {
    const bed = child.extra_bed === 'available' ? 'extra bed available' : child.extra_bed === 'not_available' ? 'no extra bed' : '';
    const price = metaPrice(child);
    return `Ages ${child.age_start}–${child.age_end}: ${[bed, price === 'charges apply' ? '' : price].filter(Boolean).join(', ') || 'welcome'}`;
  }));
  add('bed-outline', 'Extra beds and cots', [
    ...list('extra_bed').map((bed) => `Extra bed: ${metaPrice(bed)}`),
    ...list('cot').map((cot) => `Cot: ${metaPrice(cot)}`),
  ]);
  add('car-outline', 'Parking', list('parking').map((entry) => `${entry.territory_type && entry.territory_type !== 'unspecified' ? `${cap(humanize(entry.territory_type))} parking` : 'Parking'}: ${metaPrice(entry)}`));
  add('wifi-outline', 'Internet', list('internet').map((entry) => `${entry.work_area ? `${cap(humanize(entry.work_area))} Wi-Fi` : 'Wi-Fi'}: ${metaPrice(entry)}`));
  add('restaurant-outline', 'Meals', [
    ...list('meal').map((meal) => `${cap(humanize(meal.meal_type))}: ${metaPrice(meal)}`),
    ...list('children_meal').map((meal) => `Children ${meal.age_start}–${meal.age_end}, ${humanize(meal.meal_type)}: ${metaPrice(meal)}`),
  ]);
  add('bus-outline', 'Shuttle', list('shuttle').map((entry) => `${entry.destination_type && entry.destination_type !== 'unspecified' ? cap(humanize(entry.destination_type)) : 'Shuttle'}${entry.shuttle_type && entry.shuttle_type !== 'unspecified' ? ` (${humanize(entry.shuttle_type)})` : ''}: ${metaPrice(entry)}`));
  add('time-outline', 'Early or late arrival', list('check_in_check_out').map((entry) => `${cap(humanize(entry.check_in_check_out_type))}: ${metaPrice(entry)}`));
  add('cash-outline', 'Extra fees', list('add_fee').map((fee) => `${cap(humanize(fee.fee_type))}: ${metaPrice(fee)}`));
  if (meta?.no_show?.availability === 'available' && meta.no_show.time) {
    add('alert-circle-outline', 'No-show', [`Counted as a no-show after ${String(meta.no_show.time).slice(0, 5)}`]);
  }
  if (meta?.visa?.visa_support === 'support_enable') add('document-text-outline', 'Visa', ['Visa support available']);
  // The free text often repeats a structured row (e.g. the airport shuttle); keep the structured one.
  const covered = new Set(rows.map((row) => row.title));
  rows.push(...splitPolicyText(freeText).filter((row) => !(row.title === 'Airport shuttle' && covered.has('Shuttle'))));
  return rows;
}

function adaptHotel(result: Wire.HotelRates, nights: number): { hotel: HotelSummary; content: HotelContent } {
  const reviews = result.content?.reviews ?? [];
  const meta = result.content?.metapolicy_struct;
  const additionalInfoRaw = result.content?.additional_info ?? meta?.additional_info ?? meta?.additonal_info;
  const rawAdditionalInfo = [
    ...(additionalInfoRaw == null ? [] : Array.isArray(additionalInfoRaw) ? additionalInfoRaw : [additionalInfoRaw]),
    ...(result.content?.metapolicy_extra_info ? [result.content.metapolicy_extra_info] : []),
  ].filter((value): value is string => typeof value === 'string');
  const additionalInfo = rawAdditionalInfo
    .map((value) => htmlToLines(value).join('\n'))
    .filter((value) => value.length > 0);
  const policyTime = (value: { from?: string | null; to?: string | null } | null | undefined) => {
    if (!value) return null;
    if (value.from && value.to) return `${value.from}–${value.to}`;
    return value.from || value.to || null;
  };
  const policies = (result.content?.policies ?? []).map((policy) => ({
    title: policy.title || policy.name || 'Additional information',
    detail: policy.description || policy.value || '',
  })).filter((policy) => policy.detail);
  const rawScore = (result.content as any)?.rating ?? (result.content as any)?.reviewScore ?? (result.content as any)?.review_score;
  const numScore = typeof rawScore === 'number' ? rawScore : rawScore ? Number(rawScore) : null;
  const reviewScore = numScore && !Number.isNaN(numScore) && numScore > 0
    ? numScore
    : reviews.length
      ? Number((reviews.reduce((sum, review) => sum + (Number(review.rating) || 0), 0) / reviews.length).toFixed(1))
      : null;
  const rawCount = (result.content as any)?.review_count ?? (result.content as any)?.reviewCount ?? (result.content as any)?.review_score_count;
  const numCount = typeof rawCount === 'number' ? rawCount : rawCount ? parseInt(String(rawCount), 10) : null;
  const reviewCount = numCount && !Number.isNaN(numCount) && numCount > 0
    ? numCount
    : reviews.length > 0
      ? reviews.length
      : null;
  const detailedRatings = (result.content as any)?.detailed_ratings ?? (result.content as any)?.detailedRatings ?? null;
  const images: ImageSource[] = (result.content?.images_ext ?? []).map((image) => ({
    uri: resolveImageUrl(image.url, '1024x768'),
  }));
  const allRates: Rate[] = result.rates.map((rate, index) => {
    const payment = rate.payment_options.payment_types[0];
    const roomAmenities = (rate.amenities_data ?? []).map((amenity) => amenity.replace(/-/g, ' '));
    const inferredBed = roomAmenities.find((amenity) => /(?:king|queen|twin|double|single|sofa).*bed/i.test(amenity));
    // ETG §2.4: rate → static room via `rg_ext` (shared resolver, same as mobile).
    // No name matching and no hotel-photo fallback: unmatched rooms have no photos.
    const room = resolveRoom(rate.rg_ext, result.content?.room_groups, rate.room_name);
    const staticRoom = room.groups[0];
    const roomImages: ImageSource[] = room.images.map((url) => ({ uri: resolveImageUrl(url, '1024x768') }));
    // The first daily price is not necessarily the stay average. Anchor both
    // the nightly display and exact total to the supplier's whole-stay amount.
    const supplierTotal = Number(payment?.amount);
    const displayTotal = Number(payment?.show_amount);
    const hasSupplierTotal = Number.isFinite(supplierTotal) && supplierTotal > 0;
    // Guests see the supplier price plus the card fee; checkout adds the booking fee on top.
    const totalAmount = withCardFee(hasSupplierTotal ? supplierTotal : Number.isFinite(displayTotal) && displayTotal > 0 ? displayTotal : 0);
    const currencyCode = hasSupplierTotal ? payment?.currency_code ?? 'USD' : payment?.show_currency_code ?? 'USD';
    return {
      bookHash: rate.book_hash,
      roomName: rate.room_name,
      meal: { value: rate.meal_data.value, hasBreakfast: rate.meal_data.has_breakfast },
      price: { amount: String(totalAmount / Math.max(1, nights)), currencyCode },
      stayTotal: { amount: String(totalAmount), currencyCode },
      taxes: (payment?.tax_data?.taxes ?? []).map((tax) => ({
        name: tax.name,
        amount: tax.amount,
        currencyCode: tax.currency_code,
        includedBySupplier: tax.included_by_supplier,
      })),
      cancellation: {
        freeCancellationBefore: openFreeCancellationBefore(payment?.cancellation_penalties?.free_cancellation_before),
        tiers: (payment?.cancellation_penalties?.policies ?? []).map((policy) => ({
          startAt: policy.start_at,
          endAt: policy.end_at,
          amountCharge: {
            amount: policy.amount_charge,
            currencyCode: payment?.show_currency_code ?? 'USD',
          },
        })),
      },
      paymentType: payment?.type === 'hotel' ? 'hotel' : payment?.type === 'deposit' ? 'deposit' : 'now',
      isNeedCreditCardData: Boolean(payment?.is_need_credit_card_data),
      // Only genuine room photos: never substitute hotel-level images here.
      images: roomImages.slice(0, 8),
      rgHash: room.key,
      hasRoomMatch: room.groups.length > 0,
      roomGroupName: rate.room_data_trans?.main_room_type?.trim() || rate.room_name.split('(')[0].trim(),
      bedType: rate.room_data_trans?.bedding_type || inferredBed || rate.room_name.match(/(?:king|queen|twin|double|single|sofa)[^,)]*bed(?:s)?/i)?.[0] || null,
      sleeps: rate.rg_ext?.capacity ?? null,
      amenities: [...new Set([
        ...(staticRoom?.name_struct?.bathroom ? [staticRoom.name_struct.bathroom] : []),
        ...roomAmenities.filter((amenity) => amenity !== inferredBed),
      ])],
      roomSizeSqm: staticRoom?.size ?? null,
    };
  });
  // Same as mobile: list only rooms we can pair with their static room (photos,
  // size, facts). Set EXPO_PUBLIC_HIDE_UNMATCHED_ROOMS=false to show every ETG rate.
  // If nothing matches, show all rates rather than leave the hotel with no rooms.
  const matchedRates = allRates.filter((rate) => rate.hasRoomMatch);
  const rates = process.env.EXPO_PUBLIC_HIDE_UNMATCHED_ROOMS !== 'false' && matchedRates.length ? matchedRates : allRates;
  const lowestPrice = rates[0]?.price ?? { amount: '0', currencyCode: 'USD' };
  const name = result.content?.name ?? result.id.replace(/_/g, ' ');
  return {
    hotel: {
      id: result.id,
      hid: result.hid,
      name,
      starRating: result.content?.star_rating ?? 0,
      reviewScore,
      reviewCount,
      thumbnail: images[0] ?? null,
      address: result.content?.address ?? '',
      lowestPrice,
      retailPrice: lowestPrice,
      comparisonAvailable: rates.length > 0,
      rates,
    },
    content: {
      id: result.id,
      hid: result.hid,
      name,
      starRating: result.content?.star_rating ?? 0,
      address: result.content?.address ?? '',
      description: result.content?.description_struct?.flatMap((section) => section.paragraphs).join('\n\n') ?? '',
      images,
      amenities: [...new Set(result.content?.amenity_groups?.flatMap((group) => group.amenities) ?? [])],
      latitude: result.content?.latitude ?? 0,
      longitude: result.content?.longitude ?? 0,
      reviewScore,
      reviewCount,
      detailedRatings,
      reviews: reviews.map((review) => ({
        id: review.id,
        author: review.author || 'Verified guest',
        rating: typeof review.rating === 'string' ? Number(review.rating) || 0 : review.rating,
        positive: review.review_plus,
        negative: review.review_minus,
        created: review.created,
        travellerType: review.traveller_type,
        tripType: review.trip_type,
        roomName: (review as any).room_name,
        detailedReview: (review as any).detailed_review ?? (review as any).detailedReview
      })),
      checkIn: result.content?.check_in ?? policyTime(meta?.check_in),
      checkOut: result.content?.check_out ?? policyTime(meta?.check_out),
      additionalInfo,
      keyInfo: buildKeyInfo(meta, rawAdditionalInfo),
      policies,
    },
  };
}

function stayLength(checkin?: string, checkout?: string): number {
  if (!checkin || !checkout) return 1;
  const start = new Date(`${checkin}T00:00:00Z`).getTime();
  const end = new Date(`${checkout}T00:00:00Z`).getTime();
  return Math.max(1, Math.round((end - start) / 86_400_000));
}

function totalFor(price: Money, nights: number): Money {
  return { ...price, amount: String(Number(price.amount) * nights) };
}

function totalForRate(rate: Rate, nights: number): Money {
  return rate.stayTotal ?? totalFor(rate.price, nights);
}

export default function HotelScreen() {
  useCurrency((state) => state.selectedCurrency);
  const { id, checkin, checkout, occ, residency, source, searchId } = useLocalSearchParams<{
    id: string;
    checkin?: string;
    checkout?: string;
    occ?: string;
    residency?: string;
    source?: string;
    searchId?: string;
  }>();
  const viewSource: 'search' | 'cms' | 'direct' = source === 'search' || source === 'cms' ? source : 'direct';
  const router = useRouter();
  const isMember = useMember((state) => state.isMember);
  const openMemberPrompt = useMember((state) => state.openPrompt);
  const { width } = useWindowDimensions();
  const narrow = width < 760;
  const isDesktop = width >= 960;
  const scrollRef = useRef<ScrollView>(null);
  const bodyOffset = useRef(0);
  const sectionOffsets = useRef<Record<string, number>>({});
  const nights = stayLength(checkin, checkout);
  const activeResidency = residency === undefined ? getDefaultResidency() : normalizeResidency(residency);

  const [hotel, setHotel] = useState<HotelSummary | null>(null);
  const [content, setContent] = useState<HotelContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<{ rate: Rate; mode: 'room' | 'deal' } | null>(null);
  const [activeSection, setActiveSection] = useState('prices');
  const [keyInfoOpen, setKeyInfoOpen] = useState(false);
  // Measured x/width of each sticky-nav link (in the horizontal ScrollView's
  // own content coordinates), so a single indicator can slide beneath
  // whichever one is active instead of each link owning its own underline.
  const [navLinkLayouts, setNavLinkLayouts] = useState<Record<string, { x: number; width: number }>>({});
  const navIndicatorX = useRef(new Animated.Value(0)).current;
  const navIndicatorWidth = useRef(new Animated.Value(0)).current;
  const navIndicatorOpacity = useRef(new Animated.Value(0)).current;
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const layout = navLinkLayouts[activeSection];
    if (!layout) return;
    if (reduceMotion) {
      navIndicatorX.setValue(layout.x);
      navIndicatorWidth.setValue(layout.width);
      navIndicatorOpacity.setValue(1);
      return;
    }
    Animated.parallel([
      Animated.spring(navIndicatorX, { toValue: layout.x, friction: 10, tension: 65, useNativeDriver: false }),
      Animated.spring(navIndicatorWidth, { toValue: layout.width, friction: 10, tension: 65, useNativeDriver: false }),
      Animated.timing(navIndicatorOpacity, { toValue: 1, duration: 180, useNativeDriver: false }),
    ]).start();
  }, [activeSection, navLinkLayouts, navIndicatorX, navIndicatorWidth, navIndicatorOpacity, reduceMotion]);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [amenitiesExpanded, setAmenitiesExpanded] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [selectedReview, setSelectedReview] = useState<HotelReviewItem | null>(null);
  const [filters, setFilters] = useState<RoomFilters>({ freeCancellation: false, breakfast: false, payAtProperty: false });
  const [search, setSearch] = useState<SearchBarValue>({
    destination: '',
    residency: activeResidency,
    dates: { start: checkin ?? null, end: checkout ?? null },
    guests: (() => {
      const parsed = occ ? decodeOccupancy(occ) : [{ adults: DEFAULT_GUESTS.adults, children: [] }];
      return guestsFromOccupancy(parsed);
    })(),
  });

  useEffect(() => {
    if (!activeResidency) {
      setHotel(null);
      setContent(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const mockHotel = mockHotels.find((h) => String(h.hid) === String(id) || h.id === id);
    const requestStartedAt = Date.now();

    function announceLoad(loadedHotel: HotelSummary | null, errorCode: string | null) {
      track('hotel.viewed', {
        hid: Number(id),
        source: viewSource,
        searchId: searchId ?? null,
        latencyMs: Date.now() - requestStartedAt,
      });
      track('hotel.rooms_returned', {
        hid: Number(id),
        roomCount: loadedHotel?.rates.length ?? 0,
        minPrice: loadedHotel?.rates[0] ? Number(loadedHotel.rates[0].price.amount) || 0 : null,
        errorCode,
      });
    }

    api.hotel.rooms({
      hid: Number(id),
      checkin: checkin ?? '',
      checkout: checkout ?? '',
      residency: activeResidency,
      guests: decodeOccupancy(occ),
    }).then((response) => {
      if (!active) return;
      if (!response.data.hotel) {
        if (mockHotel) {
          setHotel(mockHotel);
          setContent(mockHotelContent[mockHotel.id] ?? null);
          setLoading(false);
          announceLoad(mockHotel, null);
          return;
        }
        throw new Error('hotel_unavailable');
      }
      const adapted = adaptHotel(response.data.hotel, nights);
      setHotel(adapted.hotel);
      setContent(adapted.content);
      setLoading(false);
      announceLoad(adapted.hotel, null);
    }).catch(() => {
      if (!active) return;
      if (mockHotel) {
        setHotel(mockHotel);
        setContent(mockHotelContent[mockHotel.id] ?? null);
        announceLoad(mockHotel, null);
      } else {
        setHotel(null);
        setContent(null);
        announceLoad(null, 'hotel_unavailable');
      }
      setLoading(false);
    });
    return () => { active = false; };
  }, [id, checkin, checkout, occ, activeResidency]);

  useEffect(() => {
    if (hotel) setSearch((previous) => ({ ...previous, destination: hotel.name }));
  }, [hotel]);

  const groupedRooms = useMemo(() => roomGroups(hotel?.rates ?? []), [hotel]);
  // A filter chip for an attribute no rate here actually has would only ever
  // produce "No rooms match" — hide it rather than offer a guaranteed-empty
  // filter.
  const availableFilters = useMemo(() => {
    const rates = hotel?.rates ?? [];
    return {
      freeCancellation: rates.some((rate) => isFreeCancellation(rate.cancellation)),
      breakfast: rates.some((rate) => rate.meal.hasBreakfast),
      payAtProperty: rates.some((rate) => rate.paymentType === 'hotel'),
    };
  }, [hotel]);
  const filteredRooms = useMemo(() => groupedRooms
    .map((group) => ({
      ...group,
      bedGroups: group.bedGroups
        .map((bedGroup) => ({
          ...bedGroup,
          rates: bedGroup.rates.filter((rate) => (
            (!filters.freeCancellation || isFreeCancellation(rate.cancellation))
            && (!filters.breakfast || rate.meal.hasBreakfast)
            && (!filters.payAtProperty || rate.paymentType === 'hotel')
          )),
        }))
        .filter((bedGroup) => bedGroup.rates.length > 0),
    }))
    .filter((group) => group.bedGroups.length > 0), [groupedRooms, filters]);

  // PriceComparison renders nothing when the hotel has no comparison data
  // (comparisonAvailable false, or the preview-fixture intelligence bails) —
  // the "Price check" nav tab must track that exactly, or it links to a
  // section that doesn't exist on the page.
  const hasPriceComparison = useMemo(
    () => Boolean(hotel?.comparisonAvailable && previewPriceIntelligence(hotel)),
    [hotel],
  );

  // Wait for the hotel to load before deciding: while it is still null
  // hasPriceComparison is false, which used to demote the default tab to
  // 'overview' for good. Once loaded, land on the first tab that exists.
  const landedOnFirstTab = useRef(false);
  useEffect(() => {
    if (!hotel) return;
    if (!hasPriceComparison) setActiveSection((current) => (current === 'prices' ? 'overview' : current));
    else if (!landedOnFirstTab.current) setActiveSection((current) => (current === 'overview' ? 'prices' : current));
    landedOnFirstTab.current = true;
  }, [hotel, hasPriceComparison]);

  function trackSection(key: string, y: number) {
    sectionOffsets.current[key] = y;
  }

  function updateActiveSection(y: number) {
    const marker = y + 98 - bodyOffset.current;
    const entries = Object.entries(sectionOffsets.current).sort((a, b) => a[1] - b[1]);
    const current = entries.reduce((active, [key, offset]) => marker >= offset ? key : active, hasPriceComparison ? 'prices' : 'overview');
    if (current !== activeSection) setActiveSection(current);
  }

  // Checkout is members-only. A signed-out "Book" press opens sign-in and
  // remembers the rate, so the traveller lands on checkout once signed in
  // instead of having to find the room again.
  const pendingRate = useRef<Rate | null>(null);

  useEffect(() => {
    if (isMember && pendingRate.current) {
      const rate = pendingRate.current;
      pendingRate.current = null;
      goToConfirm(rate);
    }
    // goToConfirm is recreated every render; only a sign-in should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember]);

  function goToConfirm(rate: Rate) {
    if (!isMember) {
      pendingRate.current = rate;
      openMemberPrompt();
      return;
    }
    track('hotel.room_selected', {
      hid: Number(id),
      bookHash: rate.bookHash,
      roomName: rate.roomName,
      mealName: rate.meal.value || null,
      total: { amount: Number(totalForRate(rate, nights).amount), currency: rate.price.currencyCode },
      freeCancellationBefore: rate.cancellation.freeCancellationBefore,
    });
    router.push({
      pathname: '/confirm',
      params: {
        hotelId: id,
        bookHash: rate.bookHash,
        checkin: search.dates.start ?? '',
        checkout: search.dates.end ?? '',
        occ: encodeOccupancy(search.guests.occupancy ?? [{
          adults: search.guests.adults,
          children: Array.from({ length: search.guests.children }, () => 8),
        }]),
        residency: activeResidency,
        hotelName: hotel?.name ?? '',
        hotelAddress: hotel?.address ?? '',
        hotelStars: String(hotel?.starRating ?? 0),
        hotelImage: JSON.stringify(rate.images[0] ?? content?.images[0] ?? null),
        reviewScore: String(content?.reviewScore ?? ''),
        reviewCount: String(content?.reviewCount ?? ''),
        hotelAmenities: JSON.stringify((content?.amenities ?? []).slice(0, 3)),
        mealIncluded: String(rate.meal.hasBreakfast),
        paymentType: rate.paymentType,
        freeCancellationBefore: rate.cancellation.freeCancellationBefore ?? '',
        checkInTime: content?.checkIn ?? '',
        checkOutTime: content?.checkOut ?? '',
        roomName: rate.roomName,
        roomPrice: rate.price.amount,
        // The property-payable taxes the guest saw here; checkout asks them to
        // review the rate if the supplier quotes different ones at prebook.
        propertyTaxes: JSON.stringify(rate.taxes.filter((tax) => !tax.includedBySupplier).map((tax) => ({ name: tax.name, amount: tax.amount, currencyCode: tax.currencyCode }))),
      },
    });
  }

  // Scrolls using the same offsets `updateActiveSection` already tracks from
  // each section's onLayout, rather than a fresh `measureLayout` against the
  // ScrollView — measureLayout's ancestor walk was unreliable here (some nav
  // links silently failed to scroll at all), while these offsets are already
  // proven correct since the scroll-spy underline tracks them live.
  function scrollToSection(key: string) {
    const offset = sectionOffsets.current[key];
    if (offset === undefined) return;
    scrollRef.current?.scrollTo({ y: Math.max(0, bodyOffset.current + offset - 74), animated: true });
  }

  const leadRate = hotel?.rates[0] ?? null;
  const nationalityPending = search.residency !== activeResidency;

  function handleSelectPlace(place: Place, searchVal: SearchBarValue) {
    setSearch(searchVal);
  }

  // Shared nav search: modifying any parameter here re-runs the search on the
  // results page (same component and interaction as home / results), or routes directly
  // to another hotel if a hotel is searched.
  async function submitSearch(destination: string) {
    const selectedResidency = normalizeResidency(search.residency);
    if (!selectedResidency) return;
    if (!destination.trim()) {
      router.setParams({ residency: selectedResidency });
      return;
    }
    const dates = {
      checkin: search.dates.start ?? '',
      checkout: search.dates.end ?? '',
    };
    const occ = encodeOccupancy(search.guests.occupancy ?? [{ adults: search.guests.adults, children: Array.from({ length: search.guests.children }, () => 8) }]);
    const residencyParam = selectedResidency;

    if (search.selectedDestination?.kind === 'hotel') {
      const hid = search.selectedDestination.hid ?? (search.selectedDestination.id ? Number(search.selectedDestination.id) : null);
      if (hid) {
        router.push({
          pathname: '/hotel/[id]',
          params: {
            id: String(hid),
            ...dates,
            occ,
            residency: residencyParam,
            source: 'direct',
          },
        });
        return;
      }
    }

    if (search.selectedDestination?.kind === 'city') {
      const regionId = search.selectedDestination.id;
      router.push({
        pathname: '/search',
        params: {
          destination: search.selectedDestination.label,
          region: search.selectedDestination.label,
          ...(regionId ? { regionId: String(regionId) } : {}),
          ...dates,
          occ,
          residency: residencyParam,
        },
      });
      return;
    }

    const matchedHotel = findHotelPlace(destination);
    if (matchedHotel?.hid) {
      router.push({
        pathname: '/hotel/[id]',
        params: {
          id: String(matchedHotel.hid),
          ...dates,
          occ,
          residency: residencyParam,
          source: 'direct',
        },
      });
      return;
    }

    try {
      const destResponse = await api.search.destinations({ query: destination });
      const exactHotel = exactHotelMatch(destResponse.data, destination);
      if (exactHotel) {
        router.push({
          pathname: '/hotel/[id]',
          params: {
            id: String(exactHotel.hid),
            ...dates,
            occ,
            residency: residencyParam,
            source: 'direct',
          },
        });
        return;
      }
    } catch {}

    router.push({
      pathname: '/search',
      params: {
        destination,
        region: destination,
        checkIn: search.dates.start ?? '',
        checkOut: search.dates.end ?? '',
        occ,
        residency: residencyParam,
      },
    });
  }
  const searchDestinationLabel = search.destination.trim() || content?.name || 'Search stays';
  const searchDatesLabel = formatRangeLabelCompact(search.dates.start, search.dates.end);
  const searchGuestCount = search.guests.adults + search.guests.children;
  const searchRoomCount = search.guests.occupancy?.length ?? search.guests.rooms ?? 1;
  const searchGuestsLabel = `${searchGuestCount} ${searchGuestCount === 1 ? 'guest' : 'guests'} · ${searchRoomCount} ${searchRoomCount === 1 ? 'room' : 'rooms'}`;
  const navTriggerLabel = `${searchDestinationLabel} · ${searchDatesLabel} · ${searchGuestsLabel}`;

  const hotelMetaTitle = content?.name ? `${content.name} — Book direct on SuiteScanner` : 'SuiteScanner — Direct hotel booking, no commission markup';
  const hotelMetaDescription = content?.description
    ? content.description.length > 155 ? `${content.description.slice(0, 155).trimEnd()}…` : content.description
    : content?.address
      ? `Book ${content?.name ?? 'this hotel'} direct at ${content.address} — same room, lower price than the big booking sites.`
      : undefined;

  return (
    <View style={styles.screen}>
      <Head>
        <title>{hotelMetaTitle}</title>
        {hotelMetaDescription ? <meta name="description" content={hotelMetaDescription} /> : null}
      </Head>
      <NavSearchBar
        value={search}
        onChange={setSearch}
        onSearch={() => submitSearch(search.destination)}
        onSelectDestination={(label) => submitSearch(label)}
        onSelectPlace={handleSelectPlace}
        triggerLabel={navTriggerLabel}
        summary={{ destination: searchDestinationLabel, dates: searchDatesLabel, guests: searchGuestsLabel }}
        autoExpand={!activeResidency}
        triggerAccessibilityLabel={`Search: ${navTriggerLabel}. Edit search`}
      />
      <ScrollView
        ref={scrollRef}
        style={styles.contentScroll}
        contentContainerStyle={styles.screenContent}
        stickyHeaderIndices={[1]}
        contentInsetAdjustmentBehavior="automatic"
        scrollEventThrottle={32}
        onScroll={(event) => updateActiveSection(event.nativeEvent.contentOffset.y)}
      >
      <View style={styles.heroShell}>
        {loading ? (
          <LoadingState label="Checking live rooms and prices…" />
        ) : nationalityPending || !activeResidency ? (
          <EmptyState
            icon="globe-outline"
            title={nationalityPending && search.residency ? 'Check rooms for this nationality' : 'Choose the lead guest’s nationality'}
            subtitle={nationalityPending && search.residency ? 'Rates may change with citizenship. Search again before selecting a room.' : 'Select the lead guest’s country of citizenship in the search bar to see accurate rates.'}
            {...(nationalityPending && search.residency ? { actionLabel: 'Check rooms', onAction: () => void submitSearch('') } : {})}
          />
        ) : !hotel || !content || !leadRate ? (
          <EmptyState
            icon="alert-circle-outline"
            title="No rooms available"
            subtitle="This hotel is not returning bookable rates for these dates."
            actionLabel="Back to search"
            onAction={() => router.push('/search')}
          />
        ) : (
          <View style={[styles.heroContent, narrow && styles.contentNarrow]}>
            <Pressable accessibilityRole="button" onPress={() => router.back()} style={styles.backLink}>
              <Ionicons name="arrow-back" size={17} color={colors.inkSoft} />
              <Text style={styles.backLinkText}>Back to search results</Text>
            </Pressable>

            <View style={[styles.heroDecisionBoard, narrow && styles.heroDecisionBoardNarrow]}>
              <View style={[styles.hotelInfoRow, narrow && styles.hotelInfoRowNarrow]}>
                <View style={styles.hotelIdentity}>
                  <View style={styles.nameRow}>
                    <Text selectable style={[styles.hotelName, narrow && styles.hotelNameNarrow]}>{hotel.name}</Text>
                    {hotel.starRating > 0 ? (
                      <View accessibilityLabel={`${hotel.starRating} star hotel`} style={styles.stars}>
                        {Array.from({ length: hotel.starRating }).map((_, index) => (
                          <Ionicons key={index} name="star" size={12} color="#D79400" />
                        ))}
                      </View>
                    ) : null}
                  </View>
                  {content.reviewScore ? (
                    <View style={styles.heroReviewRow}>
                      <Ionicons name="star" size={13} color="#F2A900" />
                      <Text style={styles.reviewLine}>{formatHotelPageReviewScore(content.reviewScore)}</Text>
                      {content.reviewCount ? <Text style={styles.heroReviewCount}>· {content.reviewCount.toLocaleString()} reviews</Text> : null}
                    </View>
                  ) : null}
                  <View style={styles.addressRow}>
                    <Ionicons name="location-outline" size={15} color={colors.inkFaint} />
                    <Text selectable style={styles.address}>{hotel.address}</Text>
                  </View>
                </View>
                <View style={[styles.headerPrice, narrow && styles.headerPriceNarrow]}>
                  <View style={[styles.headerPriceRow, narrow && styles.headerPriceRowNarrow]}>
                    <View style={styles.headerPriceInfo}>
                      <Text style={styles.headerPriceValue}>{formatMoney(leadRate.price)}<Text style={styles.headerPriceUnit}> avg/night</Text></Text>
                    </View>
                    <Button label={narrow ? 'View rooms →' : 'See available rooms →'} size="sm" style={[styles.headerButton, narrow && styles.headerButtonNarrow]} onPress={() => scrollToSection('rooms')} />
                  </View>
                </View>
              </View>
              <Gallery images={content.images} narrow={narrow} width={width} onPress={() => setDetail({ rate: leadRate, mode: 'room' })} />
            </View>
          </View>
        )}
      </View>

      <View style={styles.stickyNavShell}>
        {hotel && content && leadRate && !nationalityPending ? (
          <View style={styles.stickyNavFrame}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.stickyNav}>
              <Animated.View
                pointerEvents="none"
                style={[styles.navIndicator, { opacity: navIndicatorOpacity, transform: [{ translateX: navIndicatorX }], width: navIndicatorWidth }]}
              />
              {hasPriceComparison ? <SectionLink sectionKey="prices" label="Price check" active={activeSection === 'prices'} onPress={() => { setActiveSection('prices'); scrollToSection('prices'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} /> : null}
              <SectionLink sectionKey="overview" label="Overview" active={activeSection === 'overview'} onPress={() => { setActiveSection('overview'); scrollToSection('overview'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} />
              <SectionLink sectionKey="rooms" label="Rooms" active={activeSection === 'rooms'} onPress={() => { setActiveSection('rooms'); scrollToSection('rooms'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} />
              <SectionLink sectionKey="reviews" label="Reviews" active={activeSection === 'reviews'} onPress={() => { setActiveSection('reviews'); scrollToSection('reviews'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} />
              <SectionLink sectionKey="location" label="Location" active={activeSection === 'location'} onPress={() => { setActiveSection('location'); scrollToSection('location'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} />
              <SectionLink sectionKey="policies" label="Policies" active={activeSection === 'policies'} onPress={() => { setActiveSection('policies'); scrollToSection('policies'); }} onMeasure={(key, layout) => setNavLinkLayouts((current) => ({ ...current, [key]: layout }))} />
            </ScrollView>
          </View>
        ) : null}
      </View>

      <View onLayout={(event) => { bodyOffset.current = event.nativeEvent.layout.y; }} style={[styles.body, narrow && styles.contentNarrow]}>
        {hotel && content && leadRate && !nationalityPending ? (
          <>
            <View collapsable={false} onLayout={(event) => trackSection('prices', event.nativeEvent.layout.y)} style={styles.section}>
              <PriceComparison hotel={hotel} rate={leadRate} nights={nights} width={width} onSelect={() => scrollToSection('rooms')} onCompareAll={() => setComparisonOpen(true)} />
              {!isMember ? (
                <View style={[styles.memberNotice, narrow && styles.memberNoticeNarrow]}>
                  <View style={styles.memberNoticeIcon}><Ionicons name="pricetag" size={18} color={colors.pink} /></View>
                  <View style={styles.memberNoticeCopy}><Text style={styles.memberNoticeTitle}>Sign in to see our best prices</Text><Text style={styles.memberNoticeText}>It only takes a second to unlock private SuiteScanner deals.</Text></View>
                  <Button label="Sign in" size="sm" variant="secondary" style={[styles.memberNoticeButton, narrow && styles.memberNoticeButtonNarrow]} onPress={openMemberPrompt} />
                </View>
              ) : null}
            </View>

            <View collapsable={false} onLayout={(event) => trackSection('overview', event.nativeEvent.layout.y)} style={[styles.section, styles.propertySection]}>
              <View style={[styles.overviewGrid, narrow && styles.overviewGridNarrow]}>
                <View style={styles.overviewCol}>
                  <Text style={styles.overviewHeading}>About this property</Text>
                  {content.description ? <Text selectable style={styles.description} numberOfLines={descriptionExpanded ? undefined : 4}>{content.description}</Text> : null}
                  {content.description.length > 220 ? <Pressable accessibilityRole="button" onPress={() => setDescriptionExpanded((value) => !value)} style={styles.textLink}><Text style={styles.textLinkLabel}>{descriptionExpanded ? 'Show less' : 'Show more'}</Text></Pressable> : null}
                </View>
                {!narrow && content.amenities.length ? <View style={styles.overviewDivider} /> : null}
                {content.amenities.length ? (
                  <View style={styles.overviewCol}>
                    <Text style={styles.overviewHeading}>Top amenities</Text>
                    <View style={styles.topAmenityGrid}>
                      {content.amenities.slice(0, 6).map((amenity) => (
                        <View key={amenity} style={styles.topAmenityRow}><Ionicons name={getAmenityIcon(amenity)} size={16} color={colors.inkSoft} /><Text style={styles.topAmenityText} numberOfLines={1}>{amenity}</Text></View>
                      ))}
                    </View>
                  </View>
                ) : null}
              </View>
            </View>

            <View collapsable={false} onLayout={(event) => trackSection('rooms', event.nativeEvent.layout.y)} style={[styles.section, styles.roomsSection]}>
              <View style={[styles.roomsHeader, narrow && styles.roomsHeaderNarrow]}>
                <Text style={styles.sectionTitle}>Choose your room</Text>
                <View style={styles.filterRow}>
                  {availableFilters.freeCancellation ? <FilterChip label="Free cancellation" selected={filters.freeCancellation} onPress={() => setFilters((value) => ({ ...value, freeCancellation: !value.freeCancellation }))} /> : null}
                  {availableFilters.breakfast ? <FilterChip label="Breakfast included" selected={filters.breakfast} onPress={() => setFilters((value) => ({ ...value, breakfast: !value.breakfast }))} /> : null}
                  {availableFilters.payAtProperty ? <FilterChip label="Pay at property" selected={filters.payAtProperty} onPress={() => setFilters((value) => ({ ...value, payAtProperty: !value.payAtProperty }))} /> : null}
                </View>
              </View>
              <View style={[!narrow && styles.roomListingContainer]}>
                {!narrow && filteredRooms.length > 0 ? <RoomTableHeader /> : null}
                <View style={[styles.roomGroups, narrow && styles.roomGroupsNarrow]}>
                {filteredRooms.map((group) => (
                  <RoomCard
                    key={group.key}
                    group={group}
                    narrow={narrow}
                    nights={nights}
                    onRoomDetails={() => setDetail({ rate: group.bedGroups[0].rates[0], mode: 'room' })}
                    onDealDetails={(rate) => setDetail({ rate, mode: 'deal' })}
                    onSelect={goToConfirm}
                  />
                ))}
                {filteredRooms.length === 0 ? <Text style={styles.noFilterResults}>No rooms match these filters. Try removing one.</Text> : null}
                </View>
              </View>
            </View>

            <View collapsable={false} onLayout={(event) => trackSection('reviews', event.nativeEvent.layout.y)} style={styles.section}>
              <View style={styles.sectionHeading}>
                <Text style={styles.sectionTitle}>Guest Reviews</Text>
                <Text style={styles.sectionSubtitle}>Authentic feedback from verified RateHawk guests</Text>
              </View>

              <View style={styles.reviewSummaryCard}>
                <View style={[styles.reviewScoreCircle, content.reviewScore ? { backgroundColor: hotelPageReviewScoreColor(content.reviewScore) } : null]}>
                  <Text style={styles.reviewScoreValue}>{content.reviewScore ? formatHotelPageReviewScore(content.reviewScore) : '—'}</Text>
                  <Text style={styles.reviewScoreLabel}>out of 10</Text>
                </View>
                <View style={styles.reviewSummaryCopy}>
                  <Text style={styles.reviewSummaryTitle}>
                    {content.reviewScore ? (
                      content.reviewScore >= 9.5 ? 'Exceptional' :
                      content.reviewScore >= 9.0 ? 'Superb' :
                      content.reviewScore >= 8.5 ? 'Fabulous' :
                      content.reviewScore >= 8.0 ? 'Very good' :
                      content.reviewScore >= 7.0 ? 'Good' : 'Pleasant'
                    ) : 'Reviews coming soon'}
                  </Text>
                  <Text style={styles.reviewSummaryText}>
                    {content.reviewCount
                      ? `Based on ${content.reviewCount.toLocaleString()} verified guest review${content.reviewCount === 1 ? '' : 's'}.`
                      : 'We’ll show verified guest feedback here when it is available for this property.'}
                  </Text>
                </View>
              </View>

              {content.detailedRatings ? (
                <View style={styles.detailedRatingsContainer}>
                  <Text style={styles.detailedRatingsHeading}>Category Ratings</Text>
                  <View style={styles.detailedRatingsGrid}>
                    {Object.entries(content.detailedRatings)
                      .filter(([key, val]) => typeof val === 'number' && (val as number) > 0 && !['hygiene', 'wifi'].includes(key))
                      .map(([key, val]) => (
                        <View
                          key={key}
                          style={[
                            styles.ratingBarItem,
                            !narrow && (isDesktop ? styles.ratingBarItemDesktop : styles.ratingBarItemTablet)
                          ]}
                        >
                          <View style={styles.ratingBarHeader}>
                            <Text style={styles.ratingBarLabel}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </Text>
                            <Text style={styles.ratingBarValue}>{(val as number).toFixed(1)}</Text>
                          </View>
                          <View style={styles.ratingBarTrack}>
                            <View style={[styles.ratingBarFill, { width: `${Math.min(100, Math.round(((val as number) / 10) * 100))}%` }]} />
                          </View>
                        </View>
                      ))}
                  </View>
                </View>
              ) : null}

              {content.reviews?.length ? (
                <View style={styles.reviewsListSection}>
                  <View style={styles.reviewCards}>
                    {(showAllReviews ? content.reviews : content.reviews.slice(0, 3)).map((review) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        isDesktop={isDesktop}
                        narrow={narrow}
                        onSelect={() => setSelectedReview(review)}
                      />
                    ))}
                  </View>

                  {content.reviews.length > 3 ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={showAllReviews ? 'Show fewer reviews' : `Show all ${content.reviews.length} reviews`}
                      onPress={() => setShowAllReviews(!showAllReviews)}
                      style={styles.showAllReviewsBtn}
                    >
                      <Text style={styles.showAllReviewsText}>
                        {showAllReviews ? 'Show fewer reviews' : `Show all ${content.reviews.length} reviews`}
                      </Text>
                      <Ionicons
                        name={showAllReviews ? 'chevron-up' : 'chevron-down'}
                        size={16}
                        color={colors.brand}
                      />
                    </Pressable>
                  ) : null}
                </View>
              ) : null}
            </View>

            <View collapsable={false} onLayout={(event) => trackSection('location', event.nativeEvent.layout.y)} style={styles.section}>
              <View style={styles.sectionHeading}><Text style={styles.sectionTitle}>Location</Text><Text style={styles.sectionSubtitle}>A quick read on where you’ll be staying.</Text></View>
              <View style={[styles.locationPanel, narrow && styles.locationPanelNarrow]}>
                <View style={[styles.compactMap, narrow && styles.compactMapNarrow]}>
                  <View style={styles.mapRoadHorizontal} /><View style={styles.mapRoadVertical} /><View style={styles.mapRoadDiagonal} />
                  <View style={styles.mapBlockOne} /><View style={styles.mapBlockTwo} />
                  <View style={styles.mapPin}><Ionicons name="location" size={20} color={colors.white} /></View>
                </View>
                <View style={styles.locationCopy}>
                  <Text style={styles.locationTitle}>In the neighborhood</Text>
                  <Text selectable style={styles.locationAddress}>{content.address}</Text>
                  <Text style={styles.locationContext}>Well placed for exploring the local area, with the full address ready for directions and arrival planning.</Text>
                  <Pressable accessibilityRole="link" style={styles.locationAction}><Ionicons name="navigate-outline" size={14} color={colors.brand} /><Text style={styles.locationActionText}>Get directions</Text></Pressable>
                </View>
              </View>
            </View>

            <View collapsable={false} onLayout={(event) => trackSection('policies', event.nativeEvent.layout.y)} style={styles.section}>
              <View style={styles.sectionHeading}>
                <Text style={styles.sectionTitle}>Policies</Text>
                <Text style={styles.sectionSubtitle}>The exact terms for your room are shown before payment.</Text>
              </View>
              <View style={styles.policyList}>
                <PolicyRow icon="calendar-outline" title="Cancellation" detail={formatCancellationSummary(leadRate.cancellation)} />
                <PolicyRow icon="card-outline" title="Payment" detail={leadRate.paymentType === 'hotel' ? 'Pay at the property' : 'Pay securely when you book'} />
                <PolicyRow
                  icon="receipt-outline"
                  title="Taxes and fees"
                  detail={(() => {
                    const nonIncluded = leadRate.taxes?.filter((t) => !t.includedBySupplier) ?? [];
                    if (nonIncluded.length > 0) {
                      return `Payable at check-in: ${nonIncluded.map((t) => `${formatTaxName(t.name)} (${formatExactAmount(t.amount, t.currencyCode)})`).join(', ')}`;
                    }
                    return 'Inclusive of taxes';
                  })()}
                />
                {content.checkIn ? <PolicyRow icon="time-outline" title="Check-in" detail={content.checkIn} /> : null}
                {content.checkOut ? <PolicyRow icon="time-outline" title="Check-out" detail={content.checkOut} /> : null}
                {content.policies?.map((policy, index) => <PolicyRow key={`policy-${index}`} icon="information-circle-outline" title={policy.title} detail={policy.detail} />)}
                {content.keyInfo?.slice(0, 5).map((item, index) => (
                  <PolicyRow key={`key-${index}`} icon={item.icon as keyof typeof Ionicons.glyphMap} title={item.title} detail={item.detail} />
                ))}
                {content.keyInfo && content.keyInfo.length > 5 ? (
                  <Pressable accessibilityRole="button" onPress={() => setKeyInfoOpen(true)} style={styles.keyInfoToggle}>
                    <Text style={styles.textLinkLabel}>View all {content.keyInfo.length} policies and notes</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          </>
        ) : null}
      </View>

        {!loading ? <Footer /> : null}
      </ScrollView>
      <PolicyListModal visible={keyInfoOpen} items={content?.keyInfo ?? []} onClose={() => setKeyInfoOpen(false)} />
      <RoomDetailSheet
        rate={detail?.rate ?? null}
        mode={detail?.mode ?? 'room'}
        nights={nights}
        checkin={checkin}
        checkout={checkout}
        guests={search.guests.adults + search.guests.children}
        onClose={() => setDetail(null)}
        onSelect={(rate) => {
          setDetail(null);
          goToConfirm(rate);
        }}
      />
      {hotel && leadRate ? <PriceComparisonDrawer visible={comparisonOpen} hotel={hotel} rate={leadRate} nights={nights} onClose={() => setComparisonOpen(false)} /> : null}
      {selectedReview ? <ReviewDetailModal review={selectedReview} onClose={() => setSelectedReview(null)} /> : null}
    </View>
  );
}

function Gallery({ images, narrow, width, onPress }: { images: ImageSource[]; narrow: boolean; width: number; onPress: () => void }) {
  if (!images.length) {
    return <View style={[styles.galleryEmpty, narrow && styles.galleryEmptyNarrow]}><Ionicons name="image-outline" size={28} color={colors.inkFaint} /></View>;
  }
  if (narrow) {
    // Each tile carries its own Pressable, inside the ScrollView, rather than
    // one Pressable wrapping the whole scrollable track — a Pressable that's
    // an ANCESTOR of a horizontal ScrollView loses taps to the ScrollView's
    // own touch responder on real touch devices (fine with a mouse, which is
    // why this only showed up on mobile). A Pressable per tile, as the
    // ScrollView's direct child, is the pattern RN's responder negotiation
    // actually supports for "tap vs swipe" on the same element.
    // The swipe track shows the first 8; the badge counts them all, as on desktop.
    return <MobileGallery images={images.slice(0, 8)} totalCount={images.length} width={width} onPress={onPress} />;
  }
  // Up to 4 supporting tiles in a 2x2 grid; fewer available images fall back
  // to a balanced layout instead of repeating a photo to fill the grid.
  const supporting = images.slice(1, 5);
  return (
    <Pressable accessibilityRole="button" accessibilityLabel="View all hotel photos" onPress={onPress} style={styles.gallery}>
      <Image source={images[0]} resizeMode="cover" style={styles.galleryHero} />
      {supporting.length > 0 ? (
        <View style={styles.gallerySupport}>
          {supporting.map((image, index) => (
            <View key={index} style={[styles.galleryTile, supporting.length <= 2 && styles.galleryTileSparse, supporting.length === 1 && styles.galleryTileSolo]}>
              <Image source={image} resizeMode="cover" style={styles.galleryTileImage} />
              {index === supporting.length - 1 ? (
                <View style={styles.photoCountDesktop}>
                  <Ionicons name="images-outline" size={15} color={colors.ink} />
                  <Text style={styles.photoCountDesktopText}>View all photos ({images.length})</Text>
                </View>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}
    </Pressable>
  );
}

function MobileGallery({ images, totalCount, width, onPress }: { images: ImageSource[]; totalCount: number; width: number; onPress: () => void }) {
  const reduceMotion = useReducedMotion();
  const scrollX = useRef(new Animated.Value(0)).current;
  const [activeIndex, setActiveIndex] = useState(0);
  const imageWidth = width - spacing(6);
  const pageWidth = imageWidth + spacing(1);

  return (
    <View>
      <Animated.ScrollView
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.mobileGalleryTrack}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], {
          useNativeDriver: true,
          listener: (event) => {
            const index = Math.round((event as { nativeEvent: { contentOffset: { x: number } } }).nativeEvent.contentOffset.x / pageWidth);
            setActiveIndex((current) => (current === index ? current : index));
          },
        })}
      >
        {images.map((image, index) => {
          const inputRange = [(index - 1) * pageWidth, index * pageWidth, (index + 1) * pageWidth];
          const scale = reduceMotion ? 1 : scrollX.interpolate({ inputRange, outputRange: [0.94, 1, 0.94], extrapolate: 'clamp' });
          return (
            <Pressable key={index} accessibilityRole="button" accessibilityLabel="View all hotel photos" onPress={onPress}>
              <Animated.View style={{ transform: [{ scale }] }}>
                <Image source={image} resizeMode="cover" style={[styles.mobileGalleryImage, { width: imageWidth }]} />
              </Animated.View>
            </Pressable>
          );
        })}
      </Animated.ScrollView>
      {images.length > 1 ? (
        <View style={styles.galleryDots} pointerEvents="none">
          {images.map((_, index) => {
            const inputRange = [(index - 1) * pageWidth, index * pageWidth, (index + 1) * pageWidth];
            const dotWidth = reduceMotion ? (index === activeIndex ? 16 : 6) : scrollX.interpolate({ inputRange, outputRange: [6, 16, 6], extrapolate: 'clamp' });
            const dotOpacity = reduceMotion ? (index === activeIndex ? 1 : 0.5) : scrollX.interpolate({ inputRange, outputRange: [0.5, 1, 0.5], extrapolate: 'clamp' });
            return <Animated.View key={index} style={[styles.galleryDot, { width: dotWidth, opacity: dotOpacity }]} />;
          })}
        </View>
      ) : null}
      <Pressable accessibilityRole="button" accessibilityLabel="View all hotel photos" onPress={onPress} style={styles.photoCount}>
        <Ionicons name="images-outline" size={14} color={colors.white} />
        <Text style={styles.photoCountText}>{totalCount} photos</Text>
      </Pressable>
    </View>
  );
}

function PriceComparison({ hotel, rate, nights, width, onSelect, onCompareAll }: { hotel: HotelSummary; rate: Rate; nights: number; width: number; onSelect: () => void; onCompareAll: () => void }) {
  if (!hotel.comparisonAvailable) return null;
  const intelligence = previewPriceIntelligence(hotel);
  if (!intelligence) return null;
  const narrow = width < 760;
  const suiteNightly = Number(rate.price.amount);
  const publicNightly = Number(intelligence.publicPrice.amount);
  const savingPercent = publicNightly > suiteNightly ? Math.round(((publicNightly - suiteNightly) / publicNightly) * 100) : 0;
  const publicTotal = totalFor(intelligence.publicPrice, nights);
  const suiteTotal = totalForRate(rate, nights);
  const publicSupplier = intelligence.supplierQuotes[0]?.supplier ?? 'Lowest public rate';
  const freeCancel = isFreeCancellation(rate.cancellation);
  const priceCount = intelligence.supplierQuotes.length + 1;
  return (
    <View style={styles.comparisonCard}>
      <View style={[styles.comparisonHeading, narrow && styles.comparisonHeadingNarrow]}>
        <View style={styles.comparisonHeadingCopy}>
          <Text style={styles.sectionTitle}>Price check for your stay</Text>
          <Text style={styles.sectionSubtitle}>Same dates, occupancy and tax-inclusive price.</Text>
        </View>
      </View>
      <View style={[styles.comparePair, narrow && styles.comparePairNarrow]}>
        <View style={[styles.compareCol, narrow && styles.compareColNarrow]}>
          <View style={styles.compareCardHeader}>
            <View style={styles.compareCardTop}>
              <ProviderLogo supplier={publicSupplier} />
            </View>
          </View>
          <View style={styles.compareCardPrice}>
            <View style={styles.comparePriceLine}><Text style={styles.comparePrice}>{formatMoney(publicTotal)}</Text><Text style={styles.comparePriceUnit}> total</Text></View>
            <Text style={styles.compareNightly}>{formatMoney(intelligence.publicPrice)} per night</Text>
          </View>
          <View style={styles.compareBenefits}>
            <Benefit large included={freeCancel} label={freeCancel ? 'Free cancellation' : 'Non-refundable'} />
            <Benefit large included label="Inclusive of taxes" />
          </View>
        </View>
        <View style={[styles.compareCol, styles.compareColWin, narrow && styles.compareColNarrow]}>
          <View style={styles.compareCardHeader}>
            <View style={styles.compareCardTop}>
              {/* The source SVG is a padded 2000x2000 square (see TopNav's
                  logoViewport/logoImage) — contentFit="contain" alone would
                  shrink it to a tiny illegible square, so it's displayed
                  oversized and cropped to the same wordmark region as TopNav,
                  just scaled down to fit here. */}
              <View style={styles.compareBrandLogoViewport}>
                <ExpoImage
                  source={require('../../../assets/images/SuiteScanner.com Logo.svg')}
                  accessibilityLabel="SuiteScanner"
                  contentFit="contain"
                  style={styles.compareBrandLogoImage}
                />
              </View>
            </View>
            {savingPercent > 0 ? (
              <View style={styles.savingPill}><Text style={styles.savingPillText}>{savingPercent}% lower price</Text><Ionicons name="trending-down" size={12} color={colors.pink} /></View>
            ) : null}
          </View>
          <View style={styles.compareCardPrice}>
            <View style={styles.comparePriceLine}><Text style={styles.comparePrice}>{formatMoney(suiteTotal)}</Text><Text style={styles.comparePriceUnit}> total</Text></View>
            <Text style={styles.compareNightly}>{formatMoney(rate.price)} average per night</Text>
          </View>
          <View style={styles.compareBenefits}>
            {rate.meal.hasBreakfast ? <Benefit large included label="Breakfast included" /> : null}
            <Benefit large included={freeCancel} label={freeCancel ? 'Free cancellation' : 'Non-refundable'} />
            <Benefit large included label="Inclusive of taxes" />
          </View>
        </View>
      </View>
      <View style={styles.compareDivider} />
      <Pressable accessibilityRole="button" accessibilityLabel={`Compare all ${priceCount} prices`} onPress={onCompareAll} style={({ pressed }) => [styles.compareAll, narrow && styles.compareAllNarrow, pressed && styles.pressed]}>
        <View style={styles.compareAllCopy}><Text style={styles.compareAllTitle}>Compare all {priceCount} prices</Text><Text style={styles.compareAllMeta}>Rates, inclusions and latest check</Text></View>
        <View style={[styles.compareAllLogoRow, narrow && styles.compareAllLogoRowNarrow]}>
          {intelligence.supplierQuotes.map((quote) => <ProviderLogo key={quote.supplier} supplier={quote.supplier} compact />)}
          <Ionicons name="chevron-forward" size={16} color={colors.inkSoft} />
        </View>
      </Pressable>
    </View>
  );
}

function ProviderLogo({ supplier, compact = false }: { supplier: string; compact?: boolean }) {
  const source = supplier.startsWith('Booking')
    ? require('../../../assets/images/booking_logo.svg')
    : supplier.startsWith('Expedia')
      ? require('../../../assets/images/expedia_logo.svg')
      : require('../../../assets/images/agoda_logo.svg');
  return <ExpoImage source={source} contentFit="contain" style={{ width: compact ? 72 : 90, height: compact ? 22 : 28 }} accessibilityLabel={`${supplier} logo`} />;
}

function ReviewCard({
  review,
  isDesktop,
  narrow,
  onSelect,
}: {
  review: HotelReviewItem;
  isDesktop: boolean;
  narrow: boolean;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const hasText = Boolean(review.positive || review.negative);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Review by ${review.author || 'guest'}`}
      onPress={onSelect}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.reviewCard,
        !narrow && (isDesktop ? styles.reviewCardDesktop : styles.reviewCardTablet),
        hovered && styles.reviewCardHovered,
      ]}
    >
      <View style={styles.reviewCardTop}>
        <View style={styles.reviewAuthorGroup}>
          <View style={styles.authorAvatar}>
            <Text style={styles.authorAvatarText}>{(review.author || 'V')[0].toUpperCase()}</Text>
          </View>
          <View style={styles.reviewAuthorMeta}>
            <Text style={styles.reviewAuthor} numberOfLines={1}>{review.author || 'Verified guest'}</Text>
            <View style={styles.reviewMetaSubRow}>
              {review.created ? (
                <Text style={styles.reviewDate}>
                  {review.created.slice(0, 10)}
                </Text>
              ) : null}
              {review.travellerType ? (
                <Text style={styles.reviewTravellerType} numberOfLines={1}>· {review.travellerType}</Text>
              ) : null}
            </View>
          </View>
        </View>
        <View style={styles.reviewRatingBadge}>
          <Ionicons name="star" size={11} color={colors.white} />
          <Text style={styles.reviewRatingText}>{review.rating.toFixed(1)}</Text>
        </View>
      </View>

      {review.roomName ? (
        <View style={styles.reviewRoomRow}>
          <Ionicons name="bed-outline" size={13} color={colors.inkFaint} />
          <Text style={styles.reviewRoomText} numberOfLines={1}>{review.roomName}</Text>
        </View>
      ) : null}

      <View style={styles.reviewSnippets}>
        {review.positive ? (
          <View style={styles.reviewPositiveBlock}>
            <View style={styles.reviewSentimentIcon}>
              <Ionicons name="thumbs-up" size={13} color={colors.green} />
            </View>
            <Text style={styles.reviewQuoteText} numberOfLines={2}>{review.positive}</Text>
          </View>
        ) : null}

        {review.negative && !review.positive ? (
          <View style={styles.reviewNegativeBlock}>
            <View style={styles.reviewSentimentIcon}>
              <Ionicons name="alert-circle-outline" size={13} color={colors.inkFaint} />
            </View>
            <Text style={styles.reviewNegativeText} numberOfLines={2}>{review.negative}</Text>
          </View>
        ) : null}

        {!review.positive && !review.negative ? (
          <Text style={styles.reviewQuoteText}>Overall rating: {review.rating.toFixed(1)}/10</Text>
        ) : null}
      </View>

      <View style={styles.reviewCardBottom}>
        <Text style={styles.reviewCardMoreText}>
          {hasText ? 'More →' : 'View details →'}
        </Text>
      </View>
    </Pressable>
  );
}

function PolicyListModal({ visible, items, onClose }: { visible: boolean; items: Array<{ icon: string; title: string; detail: string }>; onClose: () => void }) {
  if (!visible) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.reviewModalLayer}>
        <Pressable accessibilityRole="button" accessibilityLabel="Close policies" onPress={onClose} style={styles.reviewModalScrim} />
        <View style={styles.reviewModalCard}>
          <View style={styles.reviewModalHeader}>
            <Text style={styles.keyInfoTitle}>Policies and notes</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose} style={styles.reviewModalClose}>
              <Ionicons name="close" size={20} color={colors.ink} />
            </Pressable>
          </View>
          <ScrollView>
            {items.map((item, index) => (
              <PolicyRow key={`all-key-${index}`} icon={item.icon as keyof typeof Ionicons.glyphMap} title={item.title} detail={item.detail} />
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function ReviewDetailModal({
  review,
  onClose,
}: {
  review: HotelReviewItem | null;
  onClose: () => void;
}) {
  if (!review) return null;

  return (
    <Modal visible={Boolean(review)} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.reviewModalLayer}>
        <Pressable accessibilityRole="button" accessibilityLabel="Close review details" onPress={onClose} style={styles.reviewModalScrim} />
        <View style={styles.reviewModalCard}>
          {/* Header */}
          <View style={styles.reviewModalHeader}>
            <View style={styles.reviewModalAuthorGroup}>
              <View style={styles.authorAvatar}>
                <Text style={styles.authorAvatarText}>{(review.author || 'V')[0].toUpperCase()}</Text>
              </View>
              <View style={styles.reviewAuthorMeta}>
                <Text style={styles.reviewAuthor}>{review.author || 'Verified guest'}</Text>
                <View style={styles.reviewMetaSubRow}>
                  {review.created ? (
                    <Text style={styles.reviewDate}>
                      {review.created.slice(0, 10)}
                    </Text>
                  ) : null}
                  {review.travellerType ? (
                    <Text style={styles.reviewTravellerType}>· {review.travellerType}</Text>
                  ) : null}
                </View>
              </View>
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <View style={styles.reviewRatingBadge}>
                <Ionicons name="star" size={12} color={colors.white} />
                <Text style={styles.reviewRatingText}>{review.rating.toFixed(1)}</Text>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close"
                onPress={onClose}
                style={styles.reviewModalClose}
              >
                <Ionicons name="close" size={20} color={colors.ink} />
              </Pressable>
            </View>
          </View>

          {/* Room info if present */}
          {review.roomName ? (
            <View style={styles.reviewModalRoomRow}>
              <Ionicons name="bed-outline" size={14} color={colors.inkFaint} />
              <Text style={styles.reviewRoomText}>{review.roomName}</Text>
            </View>
          ) : null}

          {/* Full review body */}
          <ScrollView style={styles.reviewModalBody} contentContainerStyle={styles.reviewModalBodyContent}>
            {review.positive ? (
              <View style={styles.reviewPositiveBlock}>
                <View style={styles.reviewSentimentIcon}>
                  <Ionicons name="thumbs-up" size={14} color={colors.green} />
                </View>
                <Text style={styles.reviewQuoteText}>{review.positive}</Text>
              </View>
            ) : null}

            {review.negative ? (
              <View style={styles.reviewNegativeBlock}>
                <View style={styles.reviewSentimentIcon}>
                  <Ionicons name="alert-circle-outline" size={14} color={colors.inkFaint} />
                </View>
                <Text style={styles.reviewNegativeText}>{review.negative}</Text>
              </View>
            ) : null}

            {!review.positive && !review.negative ? (
              <Text style={styles.reviewQuoteText}>Overall rating: {review.rating.toFixed(1)}/10</Text>
            ) : null}

            {review.detailedReview ? (
              <View style={styles.reviewModalDetailedScores}>
                <Text style={styles.reviewModalDetailedTitle}>Category Scores</Text>
                <View style={styles.reviewModalScoreGrid}>
                  {Object.entries(review.detailedReview)
                    .filter(([key, val]) => typeof val === 'number' && (val as number) > 0 && !['hygiene', 'wifi'].includes(key))
                    .map(([key, val]) => (
                      <View key={key} style={styles.reviewModalScorePill}>
                        <Text style={styles.reviewModalScoreLabel}>
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </Text>
                        <Text style={styles.reviewModalScoreVal}>{(val as number).toFixed(1)}</Text>
                      </View>
                    ))}
                </View>
              </View>
            ) : null}
          </ScrollView>

          {/* Footer */}
          <View style={styles.reviewModalFooter}>
            <Button label="Close" size="sm" variant="secondary" onPress={onClose} />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function PriceComparisonDrawer({ visible, hotel, rate, nights, onClose }: { visible: boolean; hotel: HotelSummary; rate: Rate; nights: number; onClose: () => void }) {
  const intelligence = previewPriceIntelligence(hotel);
  if (!intelligence) return null;
  const rows = [
    { supplier: 'SuiteScanner', price: totalForRate(rate, nights), member: true },
    ...intelligence.supplierQuotes.map((quote) => ({ supplier: quote.supplier, price: totalFor(quote.nightlyPrice, nights), member: false })),
  ];
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.drawerLayer}>
        <Pressable accessibilityRole="button" accessibilityLabel="Close price comparison" onPress={onClose} style={styles.drawerScrim} />
        <View style={styles.drawer}>
          <View style={styles.drawerHeader}>
            <View style={styles.drawerHeadingCopy}><Text style={styles.drawerTitle}>All prices checked</Text><Text style={styles.drawerSubtitle}>Same stay, occupancy and tax-inclusive total.</Text></View>
            <Pressable accessibilityRole="button" accessibilityLabel="Close" onPress={onClose} style={styles.drawerClose}><Ionicons name="close" size={21} color={colors.ink} /></Pressable>
          </View>
          <ScrollView contentContainerStyle={styles.drawerContent}>
            {rows.map((item, index) => (
              <View key={item.supplier} style={[styles.providerRow, item.member && styles.providerRowBest]}>
                <View style={styles.providerRowTop}>
                  {item.member ? (
                    <View style={styles.compareBrandLogoViewport}>
                      <ExpoImage
                        source={require('../../../assets/images/SuiteScanner.com Logo.svg')}
                        accessibilityLabel="SuiteScanner"
                        contentFit="contain"
                        style={styles.compareBrandLogoImage}
                      />
                    </View>
                  ) : <ProviderLogo supplier={item.supplier} />}
                  {item.member ? <View style={styles.lowestBadge}><Text style={styles.lowestBadgeText}>Lowest price</Text></View> : index === 1 ? <Text style={styles.lowestPublicLabel}>Lowest public</Text> : null}
                </View>
                <Text style={styles.providerPrice}>{formatMoney(item.price)} total</Text>
                <View style={styles.providerInclusions}><Benefit included label="Taxes included" /><Benefit included={isFreeCancellation(rate.cancellation)} label={isFreeCancellation(rate.cancellation) ? 'Free cancellation' : 'Non-refundable'} />{rate.meal.hasBreakfast ? <Benefit included label="Breakfast included" /> : null}</View>
                <View style={styles.providerStatus}><Ionicons name="checkmark-circle" size={13} color={colors.green} /><Text style={styles.providerStatusText}>Price checked just now · Available</Text></View>
              </View>
            ))}
          </ScrollView>
          <View style={styles.drawerFooter}><Ionicons name="shield-checkmark-outline" size={15} color={colors.inkSoft} /><Text style={styles.drawerFooterText}>Prices are only compared when stay details and inclusions match.</Text></View>
        </View>
      </View>
    </Modal>
  );
}

function Benefit({ included, label, large }: { included: boolean; label: string; large?: boolean }) {
  const icon: keyof typeof Ionicons.glyphMap = label === 'Breakfast included' ? 'restaurant-outline' : label === 'Pay online' ? 'card-outline' : label === 'Pay at property' ? 'wallet-outline' : included ? 'shield-checkmark-outline' : 'close-circle-outline';
  return (
    <View style={styles.benefitRow}>
      <Ionicons name={icon} size={14} color={included ? colors.green : colors.inkSoft} />
      <Text style={[styles.benefitText, large && styles.benefitTextLarge, included && styles.benefitTextPositive]}>{label}</Text>
    </View>
  );
}

function SectionLink({ sectionKey, label, active, onPress, onMeasure }: { sectionKey: string; label: string; active: boolean; onPress: () => void; onMeasure: (key: string, layout: { x: number; width: number }) => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      onLayout={(event) => onMeasure(sectionKey, { x: event.nativeEvent.layout.x, width: event.nativeEvent.layout.width })}
      style={({ pressed }) => [styles.sectionLink, pressed && styles.pressed]}
    >
      <Text style={[styles.sectionLinkText, active && styles.sectionLinkTextActive]}>{label}</Text>
    </Pressable>
  );
}

function FilterChip({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) {
  return (
    <Pressable accessibilityRole="checkbox" accessibilityState={{ checked: selected }} onPress={onPress} style={({ pressed }) => [styles.filterChip, selected && styles.filterChipSelected, pressed && styles.pressed]}>
      {selected ? <Ionicons name="checkmark" size={12} color={colors.brand} /> : null}
      <Text style={[styles.filterChipText, selected && styles.filterChipTextSelected]}>{label}</Text>
    </Pressable>
  );
}

function PolicyRow({ icon, title, detail }: { icon: keyof typeof Ionicons.glyphMap; title: string; detail: string }) {
  return (
    <View style={styles.policyRow}>
      <Ionicons name={icon} size={16} color={colors.inkSoft} />
      <View style={styles.policyCopy}>
        <Text style={styles.policyTitle}>{title}</Text>
        <Text style={styles.policyDetail}>{detail}</Text>
      </View>
    </View>
  );
}

// Column-proportion targets: room type 29% / guests-bed 18% /
// options 32% / price 21%. Uses the same nested flex hierarchy as RoomCard
// so headers align precisely with the row values below.
function RoomTableHeader() {
  return (
    <View style={styles.roomTableHeader}>
      <View style={styles.roomTableHeaderCard}>
        {/* Column 1 — room type */}
        <View style={[styles.roomTypeCol, styles.roomTypeColFlex, styles.roomTableHeaderCol]}>
          <Text style={styles.roomTableHeaderText}>Room type</Text>
        </View>

        <View style={styles.roomTableHeaderVDivider} />

        {/* Columns 2–4 */}
        <View style={[styles.roomOptionsCol, styles.roomOptionsColFlex]}>
          <View style={styles.bedGroupRow}>
            {/* Column 2 — guests & bed */}
            <View style={[styles.bedInfoCol, styles.bedInfoColFlex, styles.roomTableHeaderCol]}>
              <Text style={styles.roomTableHeaderText}>Guests & bed</Text>
            </View>

            <View style={styles.roomTableHeaderVDivider} />

            {/* Columns 3 & 4 */}
            <View style={styles.rateRowsCol}>
              <View style={styles.roomRateRowHeader}>
                {/* Column 3 — your options */}
                <View style={[styles.optionsCol, styles.optionsColFlex, styles.roomTableHeaderCol]}>
                  <Text style={styles.roomTableHeaderText}>Your options</Text>
                </View>

                <View style={styles.roomTableHeaderVDivider} />

                {/* Column 4 — price */}
                <View style={[styles.priceCol, styles.priceColFlex, styles.roomTableHeaderCol, styles.roomTableHeaderPriceCol]}>
                  <Text style={[styles.roomTableHeaderText, styles.roomTableHeaderPriceText]}>Price</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

function RoomCard({ group, nights, narrow, onRoomDetails, onDealDetails, onSelect }: { group: RoomGroup; nights: number; narrow: boolean; onRoomDetails: () => void; onDealDetails: (rate: Rate) => void; onSelect: (rate: Rate) => void }) {
  const roomAmenities = prioritizedRoomAmenities(group.amenities).slice(0, narrow ? 3 : 4);

  const roomThumbnail = group.images[0] || !narrow ? (
    <Pressable accessibilityRole="button" accessibilityLabel={`View ${group.title} photos`} onPress={onRoomDetails} style={[styles.roomThumb, narrow && styles.roomThumbNarrow]}>
      {group.images[0] ? <ExpoImage source={group.images[0]} contentFit="cover" style={styles.roomThumbImage} accessibilityLabel={`${group.title} room photo`} /> : (
        <View style={[styles.roomThumbImage, styles.roomThumbEmpty]}><Ionicons name="bed-outline" size={26} color={colors.inkFaint} /></View>
      )}
      {group.images.length > 1 ? <View style={styles.roomPhotoBadge}><Ionicons name="images-outline" size={12} color={colors.white} /><Text style={styles.roomPhotoBadgeText}>{group.images.length} photos</Text></View> : null}
    </Pressable>
  ) : null;

  const roomInfo = (
    <View style={[styles.roomInfo, narrow && styles.roomInfoNarrow]}>
      <Text style={styles.roomGroupTitle} numberOfLines={2}>{group.title}</Text>
      {group.sizeSqm ? <View style={[styles.roomSizeRow, narrow && styles.roomMetadataNarrow]}><Ionicons name="expand-outline" size={14} color={colors.inkSoft} /><Text style={styles.roomSizeText}>{Math.round(group.sizeSqm)} m²</Text></View> : null}
      {roomAmenities.length > 0 ? (
        <View style={[styles.roomFactLine, narrow && styles.roomMetadataNarrow]}>
          {roomAmenities.map((amenity) => <View key={amenity} style={styles.roomFact}><Ionicons name={getAmenityIcon(amenity)} size={14} color={colors.inkSoft} /><Text style={styles.roomFactText} numberOfLines={1}>{amenity}</Text></View>)}
        </View>
      ) : null}
      <Pressable accessibilityRole="button" onPress={onRoomDetails} style={[styles.roomDetailsRow, narrow && styles.roomDetailsRowNarrow]}><Text style={styles.roomDetailsLink}>Room details</Text><Ionicons name="arrow-forward" size={13} color={colors.brand} /></Pressable>
    </View>
  );

  const bedSections = group.bedGroups.map((bedGroup, bedIndex) => (
    <View key={bedGroup.key} style={narrow ? styles.mobileBedSection : [styles.bedGroupRow, bedIndex > 0 && styles.bedSubgroupDivided]}>
      {/* Guests / bed. Desktop: spans every rate row in this bed subgroup, top-aligned.
          Mobile: a compact subheader sitting above its own rate rows. */}
      <View style={narrow ? styles.mobileBedHeader : [styles.bedInfoCol, styles.bedInfoColFlex]}>
        <View style={styles.bedSubgroupHeading}>
          <Ionicons name="bed-outline" size={16} color={colors.inkSoft} />
          <Text style={styles.bedSubgroupTitle}>{bedTypeLabel(bedGroup.bedType)}</Text>
        </View>
        {group.sleeps ? (
          <View style={styles.bedSubgroupHeading}>
            <Ionicons name="people-outline" size={16} color={colors.inkSoft} />
            <Text style={styles.bedSleepsText}>Sleeps {group.sleeps}</Text>
          </View>
        ) : null}
      </View>

      {!narrow ? <View style={styles.roomVDividerSubtle} /> : null}

      <View style={narrow ? undefined : styles.rateRowsCol}>
        {bedGroup.rates.map((rate, index) => (
          <DealRow key={rate.bookHash} rate={rate} nights={nights} divided={index > 0} onDetails={() => onDealDetails(rate)} onSelect={() => onSelect(rate)} narrow={narrow} />
        ))}
      </View>
    </View>
  ));

  if (narrow) {
    // Dedicated mobile presentation — not the desktop table stacked
    // vertically: a compact room summary, then bed sections each holding
    // their own rate rows, using the same underlying grouped data.
    return (
      <View style={styles.roomCardNarrow}>
          <View style={styles.mobileRoomSummary}>
          {roomThumbnail}
          {roomInfo}
        </View>
        {bedSections}
      </View>
    );
  }

  return (
    <View style={styles.roomCard}>
      {/* Column 1 — room type. Shared once per room group; spans every row below. */}
      <View style={[styles.roomTypeCol, styles.roomTypeColFlex]}>
        {roomThumbnail}
        {roomInfo}
      </View>

      <View style={styles.roomVDivider} />

      {/* Columns 2–4 — one block per bed subgroup, rate rows repeating within it. */}
      <View style={[styles.roomOptionsCol, styles.roomOptionsColFlex]}>{bedSections}</View>
    </View>
  );
}

function DealRow({ rate, nights, divided, narrow, onDetails, onSelect }: { rate: Rate; nights: number; divided: boolean; narrow: boolean; onDetails: () => void; onSelect: () => void }) {
  const free = isFreeCancellation(rate.cancellation);
  const hasBreakfast = rate.meal.hasBreakfast;
  const cancellationLabel = free ? 'Free cancellation' : 'Non-refundable';
  // Compact "Cancel before <date>" summary here; the exact UTC+0 timestamp
  // (required per ETG best practice) stays one tap away via "View terms".
  const deadline = free && rate.cancellation.freeCancellationBefore ? `Cancel before ${formatUtcDateShort(rate.cancellation.freeCancellationBefore)}` : null;
  const a11yTitle = hasBreakfast ? (free ? 'Breakfast + free cancellation' : 'Breakfast included') : cancellationLabel;

  // Breakfast and cancellation are separate, independently sourced benefits,
  // each with its own line — a rate can have either, both, or neither, and
  // they must never be merged into one combined heading.
  const benefits = (
    <>
      {hasBreakfast ? (
        <View style={styles.dealBenefitRow}>
          <Ionicons name="restaurant-outline" size={15} color={colors.green} />
          <Text style={styles.dealBenefitText} numberOfLines={1}>Breakfast included</Text>
        </View>
      ) : null}
      <View style={[styles.dealBenefitRow, hasBreakfast && styles.dealBenefitRowSpaced]}>
        <Ionicons name={free ? 'shield-checkmark-outline' : 'close-circle-outline'} size={15} color={free ? colors.green : colors.inkSoft} />
        <Text style={[styles.dealBenefitText, !free && styles.dealBenefitTextMuted]} numberOfLines={1}>{cancellationLabel}</Text>
      </View>
      {deadline ? <Text style={[styles.dealDeadline, narrow && styles.dealMetadataNarrow]} numberOfLines={1}>{deadline}</Text> : null}
      <View style={[styles.dealPayRow, narrow && styles.dealMetadataNarrow]}>
        <Ionicons name={rate.paymentType === 'hotel' ? 'wallet-outline' : 'card-outline'} size={13} color={colors.inkFaint} />
        <Text style={styles.dealPayText}>{rate.paymentType === 'hotel' ? 'Pay at property' : 'Pay online'}</Text>
        <Pressable accessibilityRole="button" onPress={onDetails}><Text style={styles.dealTermsText}>· View terms</Text></Pressable>
      </View>
    </>
  );

  const priceBlock = (
    <View style={[styles.dealPriceCol, narrow && styles.dealPriceColNarrow]}>
      <Text style={styles.dealNightlyPrice}>{formatMoney(rate.price)}<Text style={styles.dealNightlyUnit}> avg/night</Text></Text>
      <Text style={styles.dealRatePriceUnit}>{formatMoney(totalForRate(rate, nights))} total</Text>
      <Text style={styles.dealRateTaxes}>Online taxes & fees included</Text>
    </View>
  );

  const bookButton = (
    <Pressable accessibilityRole="button" accessibilityLabel={`Book ${a11yTitle}`} onPress={onSelect} style={({ pressed }) => [styles.bookButton, narrow && styles.bookButtonNarrow, pressed && styles.pressed]}>
      <Text style={styles.bookButtonText}>Book</Text>
    </Pressable>
  );

  if (narrow) {
    // A deliberate stacked rate: inclusions, price, then the full-width CTA.
    return (
      <View style={[styles.mobileRateRow, divided && styles.mobileRateRowDivided]}>
        <View style={styles.mobileRateBenefits}>{benefits}</View>
        <View style={styles.mobileRatePriceRow}>{priceBlock}</View>
        {bookButton}
      </View>
    );
  }

  return (
    <View style={[styles.roomRateRow, divided && styles.roomRateRowDivided]}>
      {/* Column 3 — your options. */}
      <View style={[styles.optionsCol, styles.optionsColFlex]}>{benefits}</View>

      <View style={styles.roomVDividerSubtle} />

      {/* Column 4 — price + book, always paired. */}
      <View style={[styles.priceCol, styles.priceColFlex]}>
        {priceBlock}
        {bookButton}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.card },
  contentScroll: { flex: 1, backgroundColor: colors.card },
  screenContent: { flexGrow: 1 },
  searchStrip: { backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  searchStripHidden: { display: 'none' },
  searchStripInner: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', boxSizing: 'border-box', paddingHorizontal: spacing(3), paddingVertical: spacing(1.5) },
  mobileSearchSummary: { width: '100%', boxSizing: 'border-box', minHeight: 58, flexDirection: 'row', alignItems: 'center', gap: spacing(1), borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.card, paddingHorizontal: spacing(1.5) },
  mobileSearchCopy: { flex: 1, minWidth: 0, gap: 2 },
  mobileSearchDestination: { fontFamily: fontFamily.semibold, fontSize: 14, color: colors.ink },
  mobileSearchMeta: { fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
  mobileSearchAction: { fontFamily: fontFamily.semibold, fontSize: 12, color: colors.brand },
  // Grows so a short page (no rooms, error) still pushes the footer to the bottom.
  heroShell: { flexGrow: 1, backgroundColor: colors.card },
  heroContent: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', boxSizing: 'border-box', paddingHorizontal: spacing(3), paddingTop: spacing(2), paddingBottom: spacing(2.25), gap: spacing(1.5) },
  contentNarrow: { width: '100%', alignSelf: 'center', boxSizing: 'border-box' },
  backLink: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: spacing(0.6), minHeight: 28 },
  backLinkText: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  // Hotel info row (name/rating/address + price) sits above the gallery now,
  // instead of a tall column running beside it.
  heroDecisionBoard: { width: '100%', alignSelf: 'stretch', flexDirection: 'column', gap: spacing(2.5) },
  heroDecisionBoardNarrow: { gap: spacing(1.5) },
  hotelInfoRow: { width: '100%', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing(3) },
  hotelInfoRowNarrow: { flexDirection: 'column', gap: spacing(1.5) },
  hotelIdentity: { flex: 1, minWidth: 0, gap: spacing(1.25) },
  hotelName: { fontFamily: fontFamily.semibold, ...type.pageTitle, letterSpacing: -0.35, color: colors.ink },
  nameRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(1) },
  hotelNameNarrow: { ...typeCompact.pageTitle },
  stars: { flexDirection: 'row', gap: 2 },
  heroReviewRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(0.6), marginTop: spacing(0.25) },
  reviewBadge: { minWidth: 38, textAlign: 'center', borderRadius: 5, backgroundColor: colors.green, paddingHorizontal: 8, paddingVertical: 5, fontFamily: fontFamily.bold, fontSize: 13, color: colors.white },
  reviewLine: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  heroReviewDivider: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  heroReviewCount: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  addressRow: { width: '100%', flexDirection: 'row', alignItems: 'center', gap: spacing(0.5), maxWidth: 680 },
  address: { flexShrink: 1, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
  heroAmenities: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing(0.75), marginTop: spacing(0.75) },
  heroAmenity: { flexDirection: 'row', alignItems: 'center', gap: 3, borderWidth: 1, borderColor: colors.lineSoft, borderRadius: 5, paddingHorizontal: spacing(0.75), paddingVertical: spacing(0.5) },
  heroAmenityText: { fontFamily: fontFamily.medium, fontSize: 10, color: colors.inkSoft },
  headerPrice: { flexShrink: 0 },
  headerPriceNarrow: { width: '100%', borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.component },
  // Price info sits left of the CTA on the same row, on both desktop and
  // mobile — only a very narrow viewport is allowed to wrap it.
  headerPriceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: space.internal },
  headerPriceRowNarrow: { justifyContent: 'space-between', flexWrap: 'wrap', rowGap: space.compact },
  headerPriceInfo: { alignItems: 'flex-start' },
  headerPriceValue: { fontFamily: fontFamily.bold, fontSize: 28, lineHeight: 33, letterSpacing: -0.35, color: colors.ink },
  headerPriceUnit: { fontFamily: fontFamily.regular, fontSize: 14, color: colors.inkFaint },
  headerButton: { minHeight: 42, borderRadius: 7, paddingHorizontal: spacing(2), paddingVertical: 0 },
  headerButtonNarrow: { minHeight: 40, paddingHorizontal: spacing(1.5) },
  gallery: { width: '100%', height: 380, flexDirection: 'row', gap: 4, borderRadius: 10, overflow: 'hidden' },
  galleryHero: { flexGrow: 1, flexBasis: 0, height: '100%', backgroundColor: colors.lineSoft },
  gallerySupport: { flexGrow: 1, flexBasis: 0, flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  galleryTile: { width: '49.5%', height: '49.4%', overflow: 'hidden', backgroundColor: colors.lineSoft },
  galleryTileSparse: { width: '100%' },
  galleryTileSolo: { height: '100%' },
  galleryTileImage: { width: '100%', height: '100%' },
  galleryEmpty: { height: 260, borderRadius: 10, borderWidth: 1, borderColor: colors.line, backgroundColor: '#FAFBFC', alignItems: 'center', justifyContent: 'center' },
  galleryEmptyNarrow: { height: 210 },
  mobileGalleryTrack: { gap: spacing(1) },
  mobileGalleryImage: { height: 210, borderRadius: 9, backgroundColor: colors.lineSoft },
  galleryDots: { position: 'absolute', left: 0, right: 0, bottom: spacing(1.5), flexDirection: 'row', justifyContent: 'center', gap: spacing(0.5) },
  galleryDot: { height: 6, borderRadius: 3, backgroundColor: colors.white },
  photoCount: { position: 'absolute', right: spacing(1.5), bottom: spacing(1.5), flexDirection: 'row', alignItems: 'center', gap: spacing(0.5), backgroundColor: 'rgba(15,23,42,0.82)', paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.75), borderRadius: radii.full },
  photoCountText: { fontFamily: fontFamily.semibold, fontSize: control.badge.fontSize, color: colors.white },
  photoCountDesktop: { position: 'absolute', right: spacing(1), bottom: spacing(1), height: 34, flexDirection: 'row', alignItems: 'center', gap: spacing(0.6), backgroundColor: colors.white, paddingHorizontal: spacing(1.1), borderRadius: 7, boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)' },
  photoCountDesktopText: { fontFamily: fontFamily.semibold, fontSize: control.badge.fontSize, color: colors.ink },
  stickyNavShell: { zIndex: 10, backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  stickyNavFrame: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center' },
  stickyNav: { paddingHorizontal: spacing(3), gap: spacing(4) },
  // A single sliding indicator replaces each link owning its own underline —
  // positioned absolutely within the row (every RN View is position:relative
  // by default, so this anchors to stickyNav's content without extra setup).
  navIndicator: { position: 'absolute', left: 0, bottom: 0, height: 2, borderRadius: 1, backgroundColor: colors.brand },
  sectionLink: { minHeight: 52, justifyContent: 'center', paddingVertical: space.internal, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  sectionLinkText: { fontFamily: fontFamily.medium, fontSize: 15, color: colors.inkSoft },
  sectionLinkTextActive: { fontFamily: fontFamily.semibold, color: colors.brand },
  pressed: { opacity: 0.65 },
  body: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', paddingHorizontal: space.card, paddingBottom: space.sectionMajor, backgroundColor: colors.card },
  section: { paddingTop: space.cardLarge, gap: space.card },
  sectionTitle: { fontFamily: fontFamily.semibold, ...type.sectionTitle, letterSpacing: -0.2, color: colors.ink },
  sectionHeading: { gap: space.related },
  sectionSubtitle: { maxWidth: 720, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkFaint },
  comparisonSection: { gap: spacing(1.25) },
  comparisonHeading: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing(2) },
  comparisonHeadingNarrow: { alignItems: 'flex-start', flexWrap: 'wrap', gap: spacing(1) },
  comparisonHeadingCopy: { flex: 1, gap: spacing(0.75) },
  comparisonCard: { backgroundColor: colors.cream, borderWidth: 1, borderColor: colors.line, borderRadius: 20, paddingHorizontal: space.cardLarge, paddingTop: space.cardLarge, paddingBottom: space.cardLarge },
  comparePair: { flexDirection: 'row', alignItems: 'stretch', gap: space.component, marginTop: space.card },
  comparePairNarrow: { flexDirection: 'column' },
  compareCol: { flex: 1, minWidth: 0, minHeight: 240, flexDirection: 'column', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, padding: space.card },
  compareColNarrow: { minHeight: 0, padding: space.card },
  compareColWin: { borderColor: '#F3C9DB' },
  compareCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing(1.5) },
  compareCardTop: { gap: space.related },
  savingPill: { flexDirection: 'row', alignItems: 'center', gap: spacing(0.4), backgroundColor: colors.pinkTint, borderRadius: radii.full, paddingHorizontal: spacing(1.5), paddingVertical: spacing(0.75) },
  savingPillText: { fontFamily: fontFamily.semibold, ...price.savingsPill, color: colors.pink },
  // Scaled copy of TopNav's logoViewport/logoImage crop (same source SVG,
  // same wordmark region), just sized for this card instead of the nav bar.
  compareBrandLogoViewport: { width: 138, height: 24, overflow: 'hidden' },
  compareBrandLogoImage: { position: 'absolute', width: 147, height: 147, left: -5, top: -58 },
  compareCardPrice: { marginTop: space.internal },
  comparePriceLine: { flexDirection: 'row', alignItems: 'baseline' },
  comparePrice: { fontFamily: fontFamily.bold, ...price.featured, letterSpacing: -0.4, color: colors.ink },
  comparePriceUnit: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  compareNightly: { marginTop: space.micro, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkFaint },
  compareBenefits: { marginTop: 'auto', paddingTop: space.component, gap: space.related },
  compareDivider: { height: 1, backgroundColor: colors.lineSoft, marginTop: spacing(3.5) },
  compareAll: { minHeight: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(2), marginTop: spacing(3) },
  // Stacking (rather than wrapping the row) keeps the copy at full width on
  // mobile — sharing a wrapped row with the non-shrinking logo group was
  // squeezing the text down to a near-zero column.
  compareAllNarrow: { flexDirection: 'column', alignItems: 'flex-start', gap: spacing(1.5) },
  compareAllLogoRow: { flexDirection: 'row', alignItems: 'center', gap: spacing(2.5) },
  compareAllLogoRowNarrow: { flexWrap: 'wrap', rowGap: spacing(1), columnGap: spacing(1.25) },
  priceBenchmark: { flexDirection: 'row', alignItems: 'stretch', gap: spacing(1), borderRadius: 12, backgroundColor: '#FFF3F8', padding: spacing(1.5) },
  priceBenchmarkNarrow: { flexDirection: 'column', padding: spacing(1) },
  publicBenchmark: { flex: 1, minHeight: 248, justifyContent: 'flex-start', borderWidth: 1, borderColor: '#E3DDE1', borderRadius: 9, backgroundColor: colors.card, paddingTop: spacing(2.25), paddingRight: spacing(2.25), paddingBottom: spacing(2.25), paddingLeft: spacing(3.5) },
  suiteBenchmark: { flex: 1, minHeight: 248, borderWidth: 2, borderColor: colors.pink, borderRadius: 9, backgroundColor: colors.card, overflow: 'hidden' },
  benchmarkVs: { width: 28, alignItems: 'center', justifyContent: 'center' },
  benchmarkVsNarrow: { width: '100%', height: 22 },
  benchmarkVsText: { fontFamily: fontFamily.semibold, fontSize: 11, color: colors.inkFaint },
  benchmarkCardNarrow: { width: '100%', minHeight: 238, flexGrow: 0, flexShrink: 0, flexBasis: 'auto' },
  savingsCap: { minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing(0.75), backgroundColor: colors.pink, paddingHorizontal: spacing(2) },
  savingsCapText: { fontFamily: fontFamily.bold, fontSize: 18, letterSpacing: -0.2, color: colors.white },
  suiteBenchmarkBody: { padding: spacing(2.25) },
  benchmarkContext: { fontFamily: fontFamily.medium, fontSize: 11, color: colors.inkFaint },
  benchmarkSupplier: { marginTop: spacing(0.75), fontFamily: fontFamily.semibold, fontSize: 17, color: colors.ink },
  suiteBenchmarkBrand: { marginTop: spacing(0.75), fontFamily: fontFamily.bold, fontSize: 17, color: colors.brand },
  benchmarkPriceLine: { marginTop: spacing(2), flexDirection: 'row', alignItems: 'baseline' },
  publicBenchmarkPrice: { fontFamily: fontFamily.bold, fontSize: 28, letterSpacing: -0.4, color: colors.ink },
  suiteBenchmarkPrice: { fontFamily: fontFamily.bold, fontSize: 28, letterSpacing: -0.4, color: colors.pink },
  benchmarkPriceUnit: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkSoft },
  benchmarkNightly: { marginTop: 2, fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
  benchmarkBenefits: { marginTop: spacing(1.5), gap: spacing(0.5) },
  inclusiveText: { fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
  benchmarkButton: { width: '100%', minHeight: 38, marginTop: spacing(1.5), borderRadius: 6, paddingVertical: 0 },
  compareAllButton: { minHeight: 56, flexDirection: 'row', alignItems: 'center', gap: spacing(1.25), borderTopWidth: 1, borderTopColor: '#E7CBD8', paddingVertical: spacing(1) },
  compareAllLogos: { minWidth: 210, flexDirection: 'row', alignItems: 'center', gap: spacing(1.25) },
  compareAllCopy: { flex: 1, minWidth: 0, gap: 2 },
  compareAllTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  compareAllMeta: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  providerLogoRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  providerLogo: { fontFamily: fontFamily.bold, fontSize: type.cardTitle.fontSize },
  providerLogoCompact: { fontSize: type.metadata.fontSize },
  bookingWordmark: { flexDirection: 'row', alignItems: 'baseline' },
  bookingLogo: { color: '#003B95' },
  bookingDotCom: { color: '#0071C2' },
  expediaLogo: { color: '#111B55' },
  expediaMark: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#111B55', alignItems: 'center', justifyContent: 'center' },
  expediaMarkCompact: { width: 15, height: 15, borderRadius: 8 },
  agodaWordmark: { flexDirection: 'row', alignItems: 'baseline' },
  agodaLetter: { letterSpacing: -0.4 },
  priceProof: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card, overflow: 'hidden' },
  priceProofMain: { minHeight: 132, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), padding: spacing(2.25) },
  priceProofMainNarrow: { alignItems: 'stretch', flexDirection: 'column', gap: spacing(2), padding: spacing(2) },
  priceProofCopy: { flex: 1, gap: spacing(1) },
  priceProofAction: { width: 210, alignItems: 'flex-end', gap: 2 },
  priceProofActionNarrow: { minWidth: 0, alignItems: 'stretch' },
  proofPrice: { fontFamily: fontFamily.semibold, fontSize: 21, letterSpacing: -0.3, color: colors.ink },
  proofButton: { width: 118, minHeight: 36, borderRadius: 7, paddingVertical: 0, paddingHorizontal: spacing(1.5) },
  comparisonNote: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingHorizontal: spacing(2.25), paddingVertical: spacing(1), fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
  comparisonTrack: { gap: spacing(1.5), paddingBottom: 2 },
  comparisonDealsBand: { backgroundColor: '#FFF1F7', borderRadius: 12, padding: spacing(1.25), gap: spacing(1.25) },
  comparisonDealCard: { width: 250, minHeight: 316, borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: spacing(2.25), gap: spacing(1.5) },
  comparisonDealCardPrimary: { borderWidth: 2, borderColor: colors.pink },
  comparisonSupplier: { fontFamily: fontFamily.bold, fontSize: 17, color: colors.ink, minHeight: 28 },
  comparisonSupplierAgoda: { fontFamily: fontFamily.medium, color: '#6f6d70', letterSpacing: 0.2 },
  comparisonBenefits: { gap: spacing(1) },
  comparisonDealPrice: { marginTop: 'auto', gap: 2 },
  comparisonStrike: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft, textDecorationLine: 'line-through' },
  comparisonPriceLine: { flexDirection: 'row', alignItems: 'baseline' },
  comparisonPrice: { fontFamily: fontFamily.bold, fontSize: 27, letterSpacing: -0.4, color: colors.ink },
  comparisonNight: { fontFamily: fontFamily.regular, fontSize: 14, color: colors.ink },
  comparisonTax: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.ink },
  comparisonViewButton: { width: '100%', minHeight: 36, borderRadius: 7, paddingVertical: 0, paddingHorizontal: spacing(1) },
  comparisonViewButtonPrimary: { backgroundColor: colors.pink },
  moreComparisonDeals: { minHeight: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing(0.75), borderWidth: 1, borderColor: '#EBD7E1', borderRadius: 8, backgroundColor: colors.card },
  moreComparisonDealsText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.ink },
  saveStayCard: { width: 250, minHeight: 316, borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: spacing(2.25), gap: spacing(1.5) },
  saveStayCardNarrow: { width: 250 },
  saveStayIcon: { width: 52, height: 52, borderRadius: 26, backgroundColor: '#fbf5f1', alignItems: 'center', justifyContent: 'center', position: 'relative' },
  saveStayHeart: { fontSize: 31, lineHeight: 34, color: colors.brand },
  saveStaySpark: { position: 'absolute', top: 8, right: 8, fontSize: 16, color: '#f5c21c' },
  saveStayTitle: { marginTop: spacing(1.5), fontFamily: fontFamily.semibold, fontSize: 16, color: colors.ink },
  saveStayText: { fontFamily: fontFamily.regular, fontSize: 13, lineHeight: 19, color: colors.inkFaint },
  saveStayToggleRow: { marginTop: 'auto', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  saveStayToggleLabel: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.ink },
  saveStayToggle: { width: 48, height: 28, borderRadius: 14, backgroundColor: '#9b9798', padding: 3, justifyContent: 'center' },
  saveStayToggleKnob: { width: 22, height: 22, borderRadius: 11, backgroundColor: colors.white },
  dealCard: { width: 340, minHeight: 340, borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, borderCurve: 'continuous', backgroundColor: colors.card, padding: spacing(2.5), gap: spacing(2) },
  dealCardPrimary: { width: 382, borderWidth: 2, borderColor: colors.brand, boxShadow: '0 10px 28px rgba(22, 58, 186, 0.12)' },
  dealCardTopline: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(1) },
  dealBrand: { fontFamily: fontFamily.semibold, fontSize: 15, letterSpacing: -0.2, color: colors.brand },
  bestPriceBadge: { borderRadius: radii.full, backgroundColor: colors.greenTint, paddingHorizontal: spacing(1.1), paddingVertical: spacing(0.55) },
  bestPriceBadgeText: { fontFamily: fontFamily.semibold, fontSize: 10, color: colors.green },
  dealBenefits: { gap: spacing(0.75) },
  benefitRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing(0.75) },
  benefitText: { flex: 1, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  benefitTextLarge: { ...type.bodyRegular },
  // Matches DealRow's dealBenefitText/RoomDetailSheet's Fact: an included
  // benefit reads in green + medium weight everywhere, not just via its icon.
  benefitTextPositive: { fontFamily: fontFamily.medium, color: colors.green },
  dealPriceBlock: { gap: 3, marginTop: 'auto' },
  publicPrice: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint, textDecorationLine: 'line-through' },
  dealPriceRow: { flexDirection: 'row', alignItems: 'baseline' },
  dealPrice: { fontFamily: fontFamily.bold, fontSize: 30, letterSpacing: -0.5, color: colors.ink },
  dealPriceUnit: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
  dealTotal: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkSoft },
  dealSaving: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.green },
  competitorName: { fontFamily: fontFamily.bold, fontSize: 17, color: colors.ink },
  competitorContext: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  competitorSpacer: { flex: 1 },
  competitorPrice: { fontFamily: fontFamily.bold, fontSize: 25, color: colors.ink },
  comparisonDisclaimer: { fontFamily: fontFamily.regular, fontSize: 11, lineHeight: 16, color: colors.inkFaint },
  unavailableCard: { justifyContent: 'center', minHeight: 340, backgroundColor: colors.cream },
  unavailableTitle: { fontFamily: fontFamily.semibold, fontSize: 16, color: colors.ink },
  unavailableText: { maxWidth: 250, fontFamily: fontFamily.regular, fontSize: 13, lineHeight: 19, color: colors.inkFaint },
  privateRateNote: { minHeight: 68, flexDirection: 'row', alignItems: 'center', gap: spacing(1.25), borderWidth: 1, borderColor: '#F5C7DB', borderRadius: 9, backgroundColor: colors.pinkTint, paddingHorizontal: spacing(1.75), paddingVertical: spacing(1) },
  privateRateIcon: { width: 36, height: 36, borderRadius: radii.full, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' },
  privateRateCopy: { flex: 1, minWidth: 0, gap: spacing(0.5) },
  privateRateTitle: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.ink },
  privateRateText: { maxWidth: 760, fontFamily: fontFamily.regular, fontSize: 12, lineHeight: 18, color: colors.inkSoft },
  roomsHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(2), flexWrap: 'wrap' },
  roomsHeaderNarrow: { flexDirection: 'column', alignItems: 'flex-start', gap: spacing(1.25) },
  filterRow: { minHeight: 30, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: space.related },
  filterLabel: { marginRight: spacing(0.25), fontFamily: fontFamily.medium, fontSize: 12, color: colors.inkSoft },
  // Outer panel holds the shared header and every room-group card, so the
  // header reads as attached to the listing rather than floating above it.
  roomListingContainer: { borderWidth: 1, borderColor: '#DCE3EE', borderRadius: 18, backgroundColor: colors.card, overflow: 'hidden' },
  roomGroups: { padding: space.component, gap: space.component },
  roomGroupsNarrow: { padding: 0, gap: space.internal },
  // Room card: a 4-column row-based grid — room type | guests/bed | your
  // options | price — built from nested flex rows so column 1 spans the
  // whole card, column 2 spans its bed subgroup, and columns 3–4 repeat per
  // rate row. Column flex ratios (29/18/32/21) are shared with the header.
  roomTableHeader: { backgroundColor: '#F5F7FC', paddingHorizontal: space.component, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  roomTableHeaderCard: { flexDirection: 'row', alignItems: 'stretch', borderWidth: 1, borderColor: 'transparent' },
  roomTableHeaderCol: { paddingVertical: spacing(2.25), justifyContent: 'center' },
  roomTableHeaderPriceCol: { paddingVertical: spacing(2.25), alignItems: 'flex-end', justifyContent: 'center' },
  roomTableHeaderText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.inkSoft, textTransform: 'uppercase', letterSpacing: 0.3 },
  roomTableHeaderPriceText: { textAlign: 'right' },
  roomTableHeaderVDivider: { width: 1, backgroundColor: 'transparent' },
  roomRateRowHeader: { flexDirection: 'row', alignItems: 'stretch' },
  roomCard: { flexDirection: 'row', alignItems: 'stretch', borderWidth: 1, borderColor: '#DCE3EE', borderRadius: 18, backgroundColor: colors.card, overflow: 'hidden' },
  roomCardNarrow: { flexDirection: 'column', width: '100%', borderWidth: 1, borderColor: '#DCE3EE', borderRadius: 16, backgroundColor: colors.card, overflow: 'hidden' },
  roomTypeCol: { minWidth: 0 },
  roomTypeColFlex: { flex: 29, padding: space.component, gap: spacing(2), justifyContent: 'flex-start' },
  // 16:9 keeps the hero proportioned at any column width, instead of a fixed
  // height that either crops oddly or stretches on narrower breakpoints.
  roomThumb: { width: '100%', aspectRatio: 16 / 9, position: 'relative', borderRadius: radii.sm, overflow: 'hidden', backgroundColor: colors.lineSoft },
  roomThumbNarrow: { width: '100%', aspectRatio: 16 / 9 },
  roomThumbImage: { width: '100%', height: '100%' },
  roomThumbEmpty: { alignItems: 'center', justifyContent: 'center' },
  roomPhotoBadge: { position: 'absolute', left: spacing(0.75), bottom: spacing(0.75), flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: 'rgba(15,23,42,0.78)', paddingHorizontal: spacing(0.75), paddingVertical: 3, borderRadius: radii.full },
  roomPhotoBadgeText: { fontFamily: fontFamily.semibold, fontSize: control.badgeCompact.fontSize, color: colors.white },
  // Each child carries its own marginTop rather than a uniform parent gap,
  // since the target rhythm (10 / 14 / 16px) isn't uniform.
  roomInfo: { flex: 1, minWidth: 0 },
  roomInfoNarrow: { width: '100%' },
  roomGroupTitle: { fontFamily: fontFamily.semibold, ...type.cardTitle, color: colors.ink },
  roomSizeRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro, marginTop: spacing(1.25) },
  roomSizeText: { fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  roomFactLine: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: space.related, rowGap: space.related, marginTop: spacing(1.75) },
  roomFact: { flexDirection: 'row', alignItems: 'center', gap: space.micro, minWidth: 0 },
  roomFactText: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft, textTransform: 'capitalize' },
  roomDetailsRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro, alignSelf: 'flex-start', marginTop: space.internal },
  roomDetailsRowNarrow: { marginTop: 8 },
  roomMetadataNarrow: { marginTop: 8 },
  roomDetailsLink: { fontFamily: fontFamily.semibold, fontSize: control.secondaryCta.fontSize, color: colors.brand },
  roomVDivider: { width: 1, backgroundColor: colors.lineSoft, alignSelf: 'stretch' },
  roomVDividerSubtle: { width: 1, backgroundColor: colors.lineSoft, alignSelf: 'stretch' },
  roomOptionsCol: { minWidth: 0 },
  roomOptionsColFlex: { flex: 71, alignSelf: 'stretch' },
  // One row per bed subgroup: column 2 (bed/guests) beside a stack of that
  // subgroup's rate rows. Later subgroups get a stronger top divider so a new
  // bed configuration reads clearly as a new group, not another rate.
  bedGroupRow: { flexDirection: 'row', alignItems: 'stretch' },
  bedSubgroupDivided: { borderTopWidth: 1, borderTopColor: colors.line },
  // Top-aligned to the first rate row of its subgroup — never centered
  // against the subgroup's full height.
  bedInfoCol: { minWidth: 0, justifyContent: 'flex-start', gap: spacing(1.25) },
  bedInfoColFlex: { flex: 18, padding: space.component },
  bedSubgroupHeading: { flexDirection: 'row', alignItems: 'flex-start', gap: space.related },
  // An attribute, not a section heading — same weight class as the occupancy
  // line below it, just a stronger color.
  bedSubgroupTitle: { flex: 1, fontFamily: fontFamily.medium, fontSize: 14, lineHeight: 20, color: colors.ink, textTransform: 'capitalize' },
  bedSleepsText: { flex: 1, fontFamily: fontFamily.regular, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  rateRowsCol: { flex: 53, minWidth: 0, alignSelf: 'stretch' },
  roomRateRow: { flexDirection: 'row', alignItems: 'stretch', minHeight: 154, flexGrow: 0, flexShrink: 0 },
  roomRateRowDivided: { borderTopWidth: 1, borderTopColor: colors.lineSoft },
  optionsCol: { minWidth: 0, justifyContent: 'flex-start' },
  optionsColFlex: { flex: 32, padding: space.component },
  // — Mobile room card: a dedicated presentation, not the desktop table
  // stacked vertically. —
  mobileRoomSummary: { flexDirection: 'column', alignItems: 'stretch', gap: 16, paddingHorizontal: 16, paddingTop: 16, paddingBottom: 16 },
  mobileBedSection: { paddingHorizontal: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: colors.lineSoft },
  mobileBedHeader: { gap: 8, marginBottom: 8 },
  mobileRateRow: { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 20 },
  mobileRateRowDivided: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: 20 },
  mobileRateBenefits: {},
  mobileRatePriceRow: { marginTop: 20, marginBottom: 16 },
  dealPriceColNarrow: { minWidth: 0, alignItems: 'flex-start' },
  // Each row carries its own marginTop rather than a uniform parent gap,
  // since breakfast/cancellation/deadline/payment don't share one rhythm.
  dealBenefitRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro },
  dealBenefitRowSpaced: { marginTop: space.related },
  dealBenefitText: { flexShrink: 1, fontFamily: fontFamily.medium, fontSize: 14, lineHeight: 20, color: colors.green },
  dealBenefitTextMuted: { color: colors.inkSoft },
  dealDeadline: { marginLeft: space.component, marginTop: spacing(0.625), fontFamily: fontFamily.regular, fontSize: 13, lineHeight: 18, color: colors.inkFaint },
  dealPayRow: { marginLeft: space.component, marginTop: spacing(1.25), flexDirection: 'row', alignItems: 'center', gap: space.micro },
  dealMetadataNarrow: { marginLeft: 0 },
  dealPayText: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft },
  dealTermsText: { fontFamily: fontFamily.medium, fontSize: control.secondaryCta.fontSize, color: colors.brand },
  priceCol: { flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', gap: spacing(1.5) },
  priceColFlex: { flex: 21, padding: space.component },
  dealPriceCol: { width: '100%', alignItems: 'flex-end' },
  // Per-night price leads (primary) — total and tax disclosure follow as
  // secondary/tertiary lines, never the other way around.
  dealNightlyPrice: { fontFamily: fontFamily.bold, fontSize: 24, lineHeight: 29, letterSpacing: -0.2, color: colors.ink },
  dealNightlyUnit: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  dealRatePriceUnit: { marginTop: 4, fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkSoft },
  dealRateTaxes: { marginTop: 6, fontFamily: fontFamily.regular, fontSize: 12, lineHeight: 16, color: colors.inkFaint },
  bookButton: { width: '100%', minHeight: 40, alignItems: 'center', justifyContent: 'center', borderRadius: radii.sm, backgroundColor: colors.brand, paddingHorizontal: spacing(2.5) },
  bookButtonNarrow: { width: '100%', height: 48, minHeight: 48 },
  bookButtonText: { fontFamily: fontFamily.semibold, fontSize: control.cardCta.fontSize, color: colors.white },
  filters: { gap: spacing(1) },
  filterChip: { minHeight: 29, flexDirection: 'row', alignItems: 'center', gap: space.micro, borderWidth: 1, borderColor: colors.line, borderRadius: 15, backgroundColor: colors.card, paddingHorizontal: spacing(1.25) },
  filterChipSelected: { borderColor: colors.brand, backgroundColor: colors.skyTint },
  filterChipText: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  filterChipTextSelected: { color: colors.brand },
  memberNotice: { flexDirection: 'row', alignItems: 'center', gap: space.compact, borderRadius: radii.sm, backgroundColor: '#FFF1F7', paddingVertical: space.compact, paddingHorizontal: space.internal },
  memberNoticeIcon: { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' },
  memberNoticeNarrow: { flexWrap: 'wrap' },
  memberNoticeCopy: { flex: 1, minWidth: 190, gap: space.micro },
  memberNoticeTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  memberNoticeText: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
  memberNoticeButton: { minWidth: 120, minHeight: 38 },
  memberNoticeButtonNarrow: { width: '100%' },
  propertySection: { paddingBottom: space.internal },
  overviewGrid: { flexDirection: 'row', alignItems: 'stretch', gap: space.cardLarge },
  overviewGridNarrow: { flexDirection: 'column', gap: space.card },
  overviewCol: { flex: 1, minWidth: 0, gap: space.compact },
  overviewDivider: { width: 1, backgroundColor: colors.lineSoft },
  overviewHeading: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, letterSpacing: -0.2, color: colors.ink },
  topAmenityGrid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing(1.25) },
  topAmenityRow: { width: '50%', flexDirection: 'row', alignItems: 'center', gap: space.related, paddingRight: space.compact },
  topAmenityText: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft, textTransform: 'capitalize' },
  propertyColumns: { maxWidth: 860, flexDirection: 'row', gap: spacing(4), alignItems: 'flex-start' },
  propertyColumnsNarrow: { flexDirection: 'column', gap: spacing(3) },
  propertyColumn: { flex: 1, minWidth: 0, gap: spacing(1.5) },
  propertyColumnNarrow: { width: '100%', flexGrow: 0, flexBasis: 'auto' },
  roomsSection: { paddingTop: space.internal, paddingBottom: space.cardLarge, backgroundColor: colors.card },
  rates: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, borderCurve: 'continuous', backgroundColor: colors.card, overflow: 'hidden' },
  rateRow: { flexDirection: 'row', alignItems: 'center', gap: spacing(2), padding: spacing(2.25) },
  rateRowDivided: { borderTopWidth: 1, borderTopColor: colors.lineSoft },
  rateRowNarrow: { alignItems: 'flex-start', flexWrap: 'wrap' },
  rateThumbWrap: { borderRadius: radii.md, borderCurve: 'continuous', overflow: 'hidden' },
  rateThumb: { width: 132, height: 100, backgroundColor: colors.lineSoft },
  rateThumbNarrow: { width: 96, height: 80 },
  rateThumbEmpty: { alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.line, backgroundColor: colors.cream },
  rateInfo: { flex: 1, minWidth: 180, gap: spacing(0.4) },
  roomName: { fontFamily: fontFamily.semibold, fontSize: 16, lineHeight: 21, color: colors.ink },
  rateMeta: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
  rateMetaGood: { color: colors.green, fontFamily: fontFamily.medium },
  detailsLink: { flexDirection: 'row', alignItems: 'center', gap: 2, marginTop: spacing(0.5), alignSelf: 'flex-start' },
  detailsLinkText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.brand },
  ratePriceBlock: { minWidth: 180, alignItems: 'flex-end', gap: spacing(1) },
  ratePriceBlockNarrow: { width: '100%', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' },
  ratePriceInlineNarrow: { alignItems: 'flex-start', gap: 2 },
  ratePriceStack: { alignItems: 'flex-end', gap: 2 },
  rateGuestPrice: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint, textDecorationLine: 'line-through' },
  rateSelectLabel: { fontFamily: fontFamily.semibold, fontSize: 11, color: colors.brand },
  rateSelectRow: { flexDirection: 'row', alignItems: 'baseline', gap: spacing(0.5) },
  ratePrice: { fontFamily: fontFamily.bold, fontSize: 22, color: colors.ink },
  ratePerNight: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
  rateTotal: { fontFamily: fontFamily.medium, fontSize: 12, color: colors.inkSoft },
  noRates: { alignItems: 'center', gap: spacing(1), borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card, padding: spacing(4) },
  noRatesTitle: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink },
  clearFilters: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.brand },
  description: { maxWidth: 760, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  textLink: { alignSelf: 'flex-start', minHeight: 32, justifyContent: 'center', marginTop: -spacing(1) },
  textLinkLabel: { fontFamily: fontFamily.semibold, fontSize: control.utilityCta.fontSize, color: colors.brand },
  popularAmenities: { maxWidth: 760, gap: spacing(1.25), marginTop: spacing(0.5) },
  subsectionTitle: { fontFamily: fontFamily.semibold, fontSize: 15, lineHeight: 20, color: colors.ink },
  amenityGrid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing(1) },
  amenityRow: { width: '33.33%', flexDirection: 'row', alignItems: 'center', gap: spacing(0.75), paddingRight: spacing(1.5) },
  amenityText: { flexShrink: 1, fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkSoft },
  locationPanel: { minHeight: 176, flexDirection: 'row', alignItems: 'stretch', gap: space.internal, borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: space.card },
  locationPanelNarrow: { flexDirection: 'column' },
  compactMap: { width: '46%', minHeight: 150, position: 'relative', overflow: 'hidden', borderRadius: 8, backgroundColor: '#EAF1E9' },
  compactMapNarrow: { width: '100%' },
  mapRoadHorizontal: { position: 'absolute', left: -10, right: -10, top: '46%', height: 15, backgroundColor: colors.white, transform: [{ rotate: '-4deg' }] },
  mapRoadVertical: { position: 'absolute', top: -10, bottom: -10, left: '58%', width: 13, backgroundColor: colors.white, transform: [{ rotate: '9deg' }] },
  mapRoadDiagonal: { position: 'absolute', left: -20, bottom: 18, width: '80%', height: 10, backgroundColor: '#D8E6F4', transform: [{ rotate: '-22deg' }] },
  mapBlockOne: { position: 'absolute', width: 54, height: 32, left: 22, top: 22, borderRadius: 5, backgroundColor: '#D5E2CF' },
  mapBlockTwo: { position: 'absolute', width: 72, height: 38, right: 20, bottom: 20, borderRadius: 5, backgroundColor: '#D5E2CF' },
  mapPin: { position: 'absolute', left: '52%', top: '38%', width: 36, height: 36, borderRadius: 18, backgroundColor: colors.brand, alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(11, 61, 145, 0.22)' },
  locationCopy: { flex: 1, maxWidth: 520, justifyContent: 'center', gap: space.related, padding: 0 },
  locationTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  locationAddress: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
  locationContext: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
  locationAction: { alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: space.micro, marginTop: space.micro },
  locationActionText: { fontFamily: fontFamily.semibold, fontSize: control.utilityCta.fontSize, color: colors.brand },
  policyList: { borderWidth: 1, borderColor: colors.line, borderRadius: 9, backgroundColor: colors.card, overflow: 'hidden' },
  policyRow: { minHeight: 62, flexDirection: 'row', alignItems: 'center', gap: space.compact, paddingHorizontal: space.internal, paddingVertical: space.compact, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.lineSoft },
  keyInfoTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
  keyInfoToggle: { paddingHorizontal: space.internal, paddingVertical: space.compact },
  policyCopy: { flex: 1, gap: space.micro },
  policyTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  policyDetail: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
  noFilterResults: { borderWidth: 1, borderColor: colors.line, borderRadius: 9, padding: space.internal, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft },
  reviewSummaryCard: { flexDirection: 'row', alignItems: 'center', gap: space.internal, borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: space.card },
  reviewScoreCircle: { width: 66, height: 66, borderRadius: 33, backgroundColor: colors.brand, alignItems: 'center', justifyContent: 'center' },
  reviewScoreValue: { fontFamily: fontFamily.bold, fontSize: type.subsectionTitle.fontSize, color: colors.white },
  reviewScoreLabel: { fontFamily: fontFamily.regular, fontSize: control.badgeCompact.fontSize, color: 'rgba(255,255,255,0.85)' },
  reviewSummaryCopy: { flex: 1, gap: space.micro },
  reviewSummaryTitle: { fontFamily: fontFamily.semibold, fontSize: type.bodyLarge.fontSize, color: colors.ink },
  reviewSummaryText: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  detailedRatingsContainer: { borderWidth: 1, borderColor: colors.line, borderRadius: 10, backgroundColor: colors.card, padding: space.card, gap: space.compact },
  detailedRatingsHeading: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  detailedRatingsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: space.internal },
  ratingBarItem: { width: '100%', gap: space.micro, flexGrow: 0, flexShrink: 0 },
  ratingBarItemTablet: { width: (Platform.OS === 'web' ? 'calc(50% - 8px)' : '48.5%') as never },
  ratingBarItemDesktop: { width: (Platform.OS === 'web' ? 'calc(33.333% - 10.67px)' : '31.8%') as never },
  ratingBarHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  ratingBarLabel: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  ratingBarValue: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.ink },
  ratingBarTrack: { height: 6, borderRadius: 3, backgroundColor: colors.lineSoft, overflow: 'hidden' },
  ratingBarFill: { height: '100%', borderRadius: 3, backgroundColor: colors.brand },
  reviewsListSection: { gap: space.internal },
  reviewCards: { flexDirection: 'row', flexWrap: 'wrap', gap: space.internal },
  reviewCard: {
    width: '100%',
    height: 220,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: 10,
    backgroundColor: colors.card,
    padding: space.compact,
    gap: space.micro,
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'space-between',
    ...({ cursor: 'pointer' } as object)
  },
  reviewCardHovered: {
    borderColor: colors.brand,
    ...shadows.sm
  },
  reviewCardTablet: { width: (Platform.OS === 'web' ? 'calc(50% - 8px)' : '48.5%') as never },
  reviewCardDesktop: { width: (Platform.OS === 'web' ? 'calc(33.333% - 10.67px)' : '31.8%') as never },
  reviewCardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.related },
  reviewAuthorGroup: { flexDirection: 'row', alignItems: 'center', gap: space.compact, flex: 1 },
  authorAvatar: { width: 34, height: 34, borderRadius: 17, backgroundColor: '#EBF3FE', alignItems: 'center', justifyContent: 'center' },
  authorAvatarText: { fontFamily: fontFamily.bold, fontSize: 14, color: colors.brand },
  reviewAuthorMeta: { flex: 1, gap: 2 },
  reviewAuthor: { fontFamily: fontFamily.semibold, fontSize: type.bodyRegular.fontSize, color: colors.ink },
  reviewMetaSubRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro },
  reviewDate: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  reviewTravellerType: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  reviewRatingBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: colors.green, borderRadius: 6, paddingVertical: spacing(0.5), paddingHorizontal: spacing(1) },
  reviewRatingText: { fontFamily: fontFamily.bold, fontSize: 13, color: colors.white, fontVariant: ['tabular-nums'] },
  reviewRoomRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro },
  reviewRoomText: { flex: 1, fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  reviewSnippets: { flex: 1, gap: space.micro, overflow: 'hidden' },
  reviewPositiveBlock: { flexDirection: 'row', alignItems: 'flex-start', gap: space.compact, backgroundColor: '#F0FDF4', borderRadius: 8, padding: space.compact },
  reviewNegativeBlock: { flexDirection: 'row', alignItems: 'flex-start', gap: space.compact, backgroundColor: '#F8FAFC', borderRadius: 8, padding: space.compact },
  reviewSentimentIcon: { marginTop: 2 },
  reviewQuoteText: { flex: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.ink, lineHeight: 20 },
  reviewNegativeText: { flex: 1, fontFamily: fontFamily.regular, fontSize: type.bodyRegular.fontSize, color: colors.inkSoft, lineHeight: 20 },
  reviewCardBottom: { marginTop: 'auto', paddingTop: space.micro, flexDirection: 'row', alignItems: 'center' },
  reviewCardMoreText: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.brand },
  showAllReviewsBtn: { alignSelf: 'center', flexDirection: 'row', alignItems: 'center', gap: space.micro, borderWidth: 1, borderColor: colors.brand, borderRadius: 8, paddingVertical: spacing(1), paddingHorizontal: spacing(2), marginTop: space.micro },
  showAllReviewsText: { fontFamily: fontFamily.semibold, fontSize: control.utilityCta.fontSize, color: colors.brand },
  reviewModalLayer: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.45)', justifyContent: 'center', alignItems: 'center', padding: spacing(2) },
  reviewModalScrim: { position: 'absolute', inset: 0 },
  reviewModalCard: { width: '100%', maxWidth: 540, backgroundColor: colors.card, borderRadius: radii.lg, padding: space.card, maxHeight: '85%', ...shadows.lift, zIndex: 10 },
  reviewModalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: space.compact, borderBottomWidth: 1, borderBottomColor: colors.lineSoft, gap: space.internal },
  reviewModalAuthorGroup: { flexDirection: 'row', alignItems: 'center', gap: space.compact, flex: 1 },
  reviewModalClose: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.lineSoft },
  reviewModalRoomRow: { flexDirection: 'row', alignItems: 'center', gap: space.micro, paddingVertical: space.compact },
  reviewModalBody: { maxHeight: 380, marginVertical: space.compact },
  reviewModalBodyContent: { gap: space.compact },
  reviewModalDetailedScores: { marginTop: space.compact, paddingTop: space.compact, borderTopWidth: 1, borderTopColor: colors.lineSoft, gap: space.compact },
  reviewModalDetailedTitle: { fontFamily: fontFamily.semibold, fontSize: type.metadata.fontSize, color: colors.inkSoft, textTransform: 'uppercase', letterSpacing: 0.5 },
  reviewModalScoreGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: space.micro },
  reviewModalScorePill: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.lineSoft, paddingHorizontal: 8, paddingVertical: 4, borderRadius: radii.sm },
  reviewModalScoreLabel: { fontFamily: fontFamily.medium, fontSize: 11, color: colors.inkSoft },
  reviewModalScoreVal: { fontFamily: fontFamily.bold, fontSize: 11, color: colors.ink },
  reviewModalFooter: { flexDirection: 'row', justifyContent: 'flex-end', paddingTop: space.compact, borderTopWidth: 1, borderTopColor: colors.lineSoft },
  drawerLayer: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  drawerScrim: { position: 'absolute', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.42)' },
  drawer: { width: '100%', maxWidth: 460, height: '100%', backgroundColor: colors.card, boxShadow: '-12px 0 34px rgba(15, 23, 42, 0.16)' },
  drawerHeader: { minHeight: 82, flexDirection: 'row', alignItems: 'center', gap: space.internal, borderBottomWidth: 1, borderBottomColor: colors.lineSoft, paddingHorizontal: space.card, paddingVertical: space.internal },
  drawerHeadingCopy: { flex: 1, gap: space.micro },
  drawerTitle: { fontFamily: fontFamily.semibold, fontSize: type.sectionTitle.fontSize, letterSpacing: -0.25, color: colors.ink },
  drawerSubtitle: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
  drawerClose: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: '#F4F6F8' },
  drawerContent: { padding: space.card, gap: space.compact },
  providerRow: { gap: space.related, borderBottomWidth: 1, borderBottomColor: colors.lineSoft, paddingHorizontal: space.compact, paddingVertical: space.compact },
  providerRowBest: { borderWidth: 1, borderColor: '#F3B6D1', borderRadius: 10, backgroundColor: '#FFF3F8' },
  providerRowTop: { minHeight: 24, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.related },
  lowestBadge: { borderRadius: radii.full, backgroundColor: colors.pink, paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.625) },
  lowestBadgeText: { fontFamily: fontFamily.semibold, fontSize: control.badgeCompact.fontSize, color: colors.white },
  lowestPublicLabel: { fontFamily: fontFamily.medium, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  providerPrice: { fontFamily: fontFamily.bold, ...price.compact, letterSpacing: -0.3, color: colors.ink },
  providerInclusions: { gap: space.micro },
  providerStatus: { flexDirection: 'row', alignItems: 'center', gap: space.micro, marginTop: space.micro },
  providerStatusText: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  drawerFooter: { flexDirection: 'row', alignItems: 'center', gap: space.related, borderTopWidth: 1, borderTopColor: colors.lineSoft, padding: space.internal },
  drawerFooterText: { flex: 1, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
});
